import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, Textarea,
  FormControl, FormLabel, Box, Text, useToast, Flex, Badge, Switch,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Checkbox, Divider, Tooltip,
  RadioGroup, Radio, Stack, Spinner, Collapse,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiRotateCcw, FiUpload, FiMessageSquare, FiRefreshCw, FiChevronDown, FiChevronUp, FiPrinter, FiDownload, FiFile, FiArrowLeft, FiX } from 'react-icons/fi';
import api from '../api';
import { useInvoiceStatuses } from '../hooks/useInvoiceStatuses';
import { ProcedureViewer } from './ProceduresDashboard';
import FileUpload from './FileUpload';
import CompanySearch from './CompanySearch';

// ── Status definitions ────────────────────────────────────────────────────────
// Shared (both IN and OUT)
const SHARED_STATUSES = {
  quotation:            { label: 'Quotation (Sent)',       color: 'cyan' },
  quotation_not_sent:   { label: 'Scheduled — Upcoming',   color: 'purple' },
  future_contractual:   { label: 'Scheduled — Upcoming',   color: 'purple' },
  po_requested:         { label: 'PO Requested',           color: 'purple' },
  po_issued:            { label: 'PO Issued',              color: 'purple' },
  credited:             { label: 'Credited',               color: 'teal' },
  obsolete:             { label: 'Obsolete',               color: 'gray' },
  superseded_quote:     { label: 'Superseded Quote',       color: 'orange' },
  deleted:              { label: 'Deleted',                color: 'red' },
};
// OUT-only (invoices to clients — money IN)
const OUT_STATUSES = {
  draft:                   { label: 'Draft',              color: 'gray' },
  to_invoice:              { label: 'To Invoice',         color: 'yellow' },
  created_not_sent:        { label: 'Created (Not Sent)', color: 'gray' },
  sent_for_payment:        { label: 'Sent for Payment',   color: 'blue' },
  invoiced_within_terms:   { label: 'Invoiced',           color: 'blue' },
  paid_in_part:            { label: 'Part Paid',          color: 'orange' },
  paid_in_full:            { label: 'Paid in Full',       color: 'green' },
  overdue:                 { label: 'Overdue',            color: 'red' },
  very_overdue:            { label: 'Very Overdue',       color: 'red' },
  written_off:             { label: 'Written Off',        color: 'gray' },
  send_for_debt_collection:{ label: 'Debt Collection',    color: 'red' },
  receipt:                 { label: 'Receipt',            color: 'green' },
  opportunity_value:       { label: 'Opportunity Value',  color: 'orange' },
};
// IN-only (supplier invoices — money OUT)
const IN_STATUSES = {
  recv_pay_within_terms: { label: 'Invoice Received — PAY WITHIN TERMS', color: 'orange' },
  recv_do_not_pay:       { label: 'Invoice Received — Do NOT Pay Yet',   color: 'red' },
  recv_assign_po:        { label: 'Invoice Received — Assign PO Number', color: 'purple' },
  recv_refused:          { label: 'Invoice Received — REFUSED',          color: 'red' },
  recv_overdue:          { label: 'Invoice Received — OVERDUE',          color: 'red' },
  recv_receipt:          { label: 'Receipt for Payment',                  color: 'green' },
  recv_credited:         { label: 'Invoice Credited by Supplier',        color: 'teal' },
  paid_in_full:          { label: 'Paid in Full',                        color: 'green' },
};

function getStatusOptions(isIn) {
  if (isIn) return { ...SHARED_STATUSES, ...IN_STATUSES };
  return { ...SHARED_STATUSES, ...OUT_STATUSES };
}

function statusLabel(status, isIn) {
  const opts = getStatusOptions(isIn);
  return opts[status]?.label || status?.replace(/_/g,' ') || status;
}
function statusColor(status, isIn) {
  const opts = getStatusOptions(isIn);
  return opts[status]?.color || 'gray';
}

// ── Inline Add Bank Account form ──────────────────────────────────────────────
function InlineAddBankAccount({ companyId, onCreated }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ bank_name: '', account_name: '', account_number: '', sort_code: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleCreate = async () => {
    if (!form.bank_name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/bank-accounts', { ...form, company_id: companyId || null, is_own: false });
      toast({ title: 'Bank account added', status: 'success', duration: 1500 });
      onCreated(data);
      setShow(false);
      setForm({ bank_name: '', account_name: '', account_number: '', sort_code: '' });
    } catch(e) {
      toast({ title: 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  if (!show) return (
    <Button size="xs" variant="link" colorScheme="blue" onClick={() => setShow(true)}>+ Add bank account</Button>
  );
  return (
    <Box bg="blue.50" p={2} borderRadius="md" border="1px" borderColor="blue.200">
      <Text fontSize="9px" fontWeight="700" color="blue.700" textTransform="uppercase" mb={1}>New Bank Account</Text>
      <VStack spacing={1}>
        <HStack w="100%" spacing={1}>
          <Input size="xs" placeholder="Bank name *" value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} />
          <Input size="xs" placeholder="Account name" value={form.account_name} onChange={e => setForm(p => ({ ...p, account_name: e.target.value }))} />
        </HStack>
        <HStack w="100%" spacing={1}>
          <Input size="xs" placeholder="Account number" value={form.account_number} onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))} />
          <Input size="xs" placeholder="Sort code" value={form.sort_code} onChange={e => setForm(p => ({ ...p, sort_code: e.target.value }))} />
        </HStack>
        <HStack justify="flex-end" w="100%">
          <Button size="xs" variant="ghost" onClick={() => setShow(false)}>Cancel</Button>
          <Button size="xs" colorScheme="blue" isLoading={saving} onClick={handleCreate}>Add</Button>
        </HStack>
      </VStack>
    </Box>
  );
}

// ── Post-create upload modal ──────────────────────────────────────────────────
function InvoiceUploadModal({ invoiceId, projectId, taskId, invoiceRef, isCreditNote, isOpen, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [creditReason, setCreditReason] = useState('');
  const [done, setDone] = useState(false);
  const toast = useToast();

  const handleSkip = () => { onClose(); };

  const handleFileUploaded = async (file) => {
    if (isCreditNote && creditReason) {
      try {
        await api.post('/notes', {
          project_id: projectId, task_id: taskId || null,
          subject: 'Credit note received',
          body: `Credit note received for invoice ${invoiceRef || invoiceId}. Reason: ${creditReason}`,
          entity_type: 'supplier',
        });
      } catch(e) {}
    }
    setDone(true);
    toast({ title: isCreditNote ? 'Credit note uploaded' : 'Invoice document uploaded', status: 'success', duration: 2000 });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          {isCreditNote ? 'Upload Credit Note' : 'Upload Invoice Document'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            {!done ? (
              <>
                <Text fontSize="12px" color="gray.600">
                  {isCreditNote
                    ? 'Upload a copy of the credit note. It will be attached to the invoice and saved to the task documents.'
                    : 'Upload a copy of the received invoice. It will be attached to this invoice record and saved to the task documents.'}
                </Text>
                {isCreditNote && (
                  <FormControl>
                    <FormLabel fontSize="xs" mb={0.5}>Reason for credit note *</FormLabel>
                    <Textarea size="sm" rows={2} value={creditReason}
                      onChange={e => setCreditReason(e.target.value)}
                      placeholder="e.g. Goods returned, pricing error, cancelled order..." />
                  </FormControl>
                )}
                {(!isCreditNote || creditReason.trim()) && (
                  <FileUpload
                    entityType="invoice" entityId={invoiceId}
                    folder="invoices" accept="image/*,.pdf"
                    label={isCreditNote ? 'Upload Credit Note (PDF/image)' : 'Upload Invoice (PDF/image)'}
                    onUploaded={handleFileUploaded}
                  />
                )}
              </>
            ) : (
              <Box textAlign="center" py={4}>
                <Text fontSize="24px" mb={2}>✅</Text>
                <Text fontSize="13px" fontWeight="600" color="green.700">
                  {isCreditNote ? 'Credit note uploaded' : 'Invoice document uploaded'}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            {!done && <Button size="sm" variant="ghost" onClick={handleSkip}>Skip for now</Button>}
            <Button size="sm" bg="brand.600" color="white" _hover={{ bg:'brand.700' }} onClick={onClose}>
              {done ? 'Done' : 'Close'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Invoice document cell (versioned attachment for received invoices) ────────
const INV_DOC_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.rtf', '.odt', '.ods',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.heic', '.tiff',
  '.zip', '.7z', '.rar',
].join(',');

function InvoiceDocumentCell({ invoice, onChanged }) {
  const [state, setState] = useState({ current: null, history: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [restoreFor, setRestoreFor] = useState(null);
  const [restoreNote, setRestoreNote] = useState('');
  const dropRef = useRef(null);
  const replaceRef = useRef(null);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/invoices/${invoice.id}/document`); setState(data && typeof data === 'object' ? data : { current: null, history: [] }); }
    catch (e) { setState({ current: null, history: [] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoice.id]);

  const uploadToFiles = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'documents');
    fd.append('entity_type', 'project_document');
    const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.id;
  };

  const doAttach = async (file, changeNotes) => {
    if (!file) return;
    setBusy(true);
    try {
      const fileId = await uploadToFiles(file);
      await api.post(`/invoices/${invoice.id}/document`, { file_id: fileId, change_notes: changeNotes || null });
      toast({ title: state.current ? 'New version uploaded' : 'Document attached', status: 'success', duration: 1800 });
      setReplacing(false); setNote('');
      await load(); onChanged && onChanged();
    } catch (e) { toast({ title: e.response?.data?.error || 'Upload failed', status: 'error', duration: 2500 }); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Remove the current document? It stays available under obsolete versions and can be restored.')) return;
    setBusy(true);
    try { await api.delete(`/invoices/${invoice.id}/document`); toast({ title: 'Document removed', status: 'info', duration: 1800 }); await load(); onChanged && onChanged(); }
    catch (e) { toast({ title: 'Failed to remove', status: 'error', duration: 2000 }); }
    finally { setBusy(false); }
  };

  const doRestore = async (versionId) => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/document/restore`, { version_id: versionId, change_notes: restoreNote || null });
      toast({ title: 'Version restored as current', status: 'success', duration: 1800 });
      setRestoreFor(null); setRestoreNote(''); await load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Restore failed', status: 'error', duration: 2000 }); }
    finally { setBusy(false); }
  };

  const fileHref = (d) => (d?.stored_name && d?.folder) ? `/api/files/${d.folder}/${d.stored_name}` : null;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '';
  const { current, history } = state;

  return (
    <Box mt={2} p={2.5} bg="white" border="1px" borderColor="gray.200" borderRadius="md">
      <HStack justify="space-between" mb={1.5}>
        <HStack spacing={1.5} color="gray.600"><FiFile size={12} /><Text fontSize="10px" fontWeight="700" textTransform="uppercase">Invoice Document</Text></HStack>
        {history.length > 0 && (
          <Button size="xs" variant="link" colorScheme="gray" rightIcon={showHistory ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />}
            onClick={() => setShowHistory(v => !v)}>
            {history.length} previous version{history.length !== 1 ? 's' : ''}
          </Button>
        )}
      </HStack>

      {loading ? (
        <HStack py={2}><Spinner size="xs" /><Text fontSize="10px" color="gray.400">Loading…</Text></HStack>
      ) : current ? (
        <Box>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Badge colorScheme="teal" fontSize="9px">v{current.version}</Badge>
            <Button as="a" href={fileHref(current)} target="_blank" rel="noopener noreferrer" size="xs" variant="ghost"
              colorScheme="blue" leftIcon={<FiDownload size={11} />} download={current.original_name}>
              {current.original_name || 'View / Download'}
            </Button>
            {current.file_size > 0 && <Text fontSize="9px" color="gray.400">{(current.file_size / 1024).toFixed(0)} KB</Text>}
            <Text fontSize="9px" color="gray.400">Uploaded {fmtDate(current.created_at)}{current.created_by_name ? ` by ${current.created_by_name}` : ''}</Text>
            <HStack spacing={1} ml="auto">
              <Button size="xs" variant="outline" colorScheme="orange" leftIcon={<FiUpload size={10} />} isLoading={busy}
                onClick={() => setReplacing(v => !v)}>Replace</Button>
              <IconButton size="xs" variant="ghost" colorScheme="red" aria-label="Delete document" icon={<FiTrash2 size={12} />}
                isLoading={busy} onClick={doDelete} />
            </HStack>
          </Flex>
          {current.change_notes && <Text fontSize="10px" color="orange.600" fontStyle="italic" mt={0.5}>"{current.change_notes}"</Text>}

          {replacing && (
            <Box mt={2} p={2} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.200">
              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0.5}>Change log (what's new in this version)</FormLabel>
                <Textarea size="xs" rows={2} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Corrected VAT, supplier reissued invoice…" />
              </FormControl>
              <input type="file" accept={INV_DOC_ACCEPT} ref={replaceRef} style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) doAttach(f, note); }} />
              <HStack justify="flex-end">
                <Button size="xs" variant="ghost" onClick={() => { setReplacing(false); setNote(''); }}>Cancel</Button>
                <Button size="xs" colorScheme="orange" leftIcon={<FiUpload size={10} />} isLoading={busy}
                  onClick={() => replaceRef.current?.click()}>Choose replacement file</Button>
              </HStack>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          ref={dropRef}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) doAttach(f, 'Initial upload'); }}
          onClick={() => dropRef.current?.querySelector('input[type=file]')?.click()}
          cursor="pointer" textAlign="center" py={4} px={3} borderRadius="md"
          border="2px dashed" borderColor={dragOver ? 'teal.400' : 'gray.300'} bg={dragOver ? 'teal.50' : 'gray.50'}
          _hover={{ borderColor: 'teal.400', bg: 'teal.50' }}
        >
          <input type="file" accept={INV_DOC_ACCEPT} style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; if (f) doAttach(f, 'Initial upload'); }} />
          {busy ? <Spinner size="sm" /> : (
            <>
              <Box color="gray.400" mb={1}><FiUpload size={18} style={{ display: 'inline' }} /></Box>
              <Text fontSize="11px" color="gray.600" fontWeight="600">Drag &amp; drop a document here, or click to select</Text>
              <Text fontSize="9px" color="gray.400" mt={0.5}>PDF, spreadsheet, Word, image, zip…</Text>
            </>
          )}
          {history.length > 0 && (
            <Text fontSize="9px" color="blue.500" mt={1}>An earlier version is available below — expand to restore it.</Text>
          )}
        </Box>
      )}

      {/* Version history (older + obsolete), restorable */}
      <Collapse in={showHistory && history.length > 0}>
        <Table size="sm" variant="simple" mt={2}>
          <Thead>
            <Tr bg="gray.50">
              <Th px={2} fontSize="9px">Ver</Th>
              <Th px={2} fontSize="9px">File</Th>
              <Th px={2} fontSize="9px">Change log</Th>
              <Th px={2} fontSize="9px">Uploaded</Th>
              <Th px={2} fontSize="9px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {history.map(h => (
              <React.Fragment key={h.id}>
                <Tr opacity={h.deleted_at ? 0.7 : 1}>
                  <Td px={2} fontSize="10px"><Badge colorScheme={h.deleted_at ? 'red' : 'gray'} fontSize="8px">v{h.version}{h.deleted_at ? ' · obsolete' : ''}</Badge></Td>
                  <Td px={2} fontSize="10px">
                    {fileHref(h) ? (
                      <Button as="a" href={fileHref(h)} target="_blank" rel="noopener noreferrer" size="xs" variant="link" colorScheme="blue"
                        leftIcon={<FiDownload size={10} />} download={h.original_name}>{h.original_name || 'File'}</Button>
                    ) : <Text fontSize="10px" color="gray.400">—</Text>}
                  </Td>
                  <Td px={2} fontSize="10px" color="gray.600">{h.change_notes || '—'}</Td>
                  <Td px={2} fontSize="9px" color="gray.400">{fmtDate(h.created_at)}{h.created_by_name ? ` · ${h.created_by_name}` : ''}</Td>
                  <Td px={2}>
                    <Button size="xs" variant="ghost" colorScheme="green" leftIcon={<FiRotateCcw size={10} />}
                      onClick={() => { setRestoreFor(restoreFor === h.id ? null : h.id); setRestoreNote(''); }}>
                      Make current
                    </Button>
                  </Td>
                </Tr>
                {restoreFor === h.id && (
                  <Tr>
                    <Td px={2} colSpan={5} bg="green.50">
                      <FormControl mb={2}>
                        <FormLabel fontSize="xs" mb={0.5}>Change log for restore</FormLabel>
                        <Textarea size="xs" rows={2} value={restoreNote} onChange={e => setRestoreNote(e.target.value)}
                          placeholder={`e.g. Reverted to v${h.version} — latest was incorrect`} />
                      </FormControl>
                      <HStack justify="flex-end">
                        <Button size="xs" variant="ghost" onClick={() => setRestoreFor(null)}>Cancel</Button>
                        <Button size="xs" colorScheme="green" leftIcon={<FiRotateCcw size={10} />} isLoading={busy}
                          onClick={() => doRestore(h.id)}>Restore as current</Button>
                      </HStack>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </Collapse>
    </Box>
  );
}

// ── Main InvoiceModal ─────────────────────────────────────────────────────────
export default function InvoiceModal({ projectId, taskId, direction = 'out', isOpen, onClose, initialStatus, launchAction }) {
  const isIn = direction === 'in';
  const [invoices, setInvoices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [regenerating, setRegenerating] = useState(false);

  const toggleExpand = async (invId) => {
    if (expandedInvoiceId === invId) { setExpandedInvoiceId(null); return; }
    setExpandedInvoiceId(invId);
    if (!expandedItems[invId]) {
      try {
        const { data } = await api.get(`/invoices/${invId}`);
        setExpandedItems(prev => ({ ...prev, [invId]: data.items || [] }));
      } catch(e) { setExpandedItems(prev => ({ ...prev, [invId]: [] })); }
    }
  };
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]); // our own accounts
  const [supplierBankAccounts, setSupplierBankAccounts] = useState([]); // supplier accounts
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [supplierCompanyName, setSupplierCompanyName] = useState('');
  const [departments, setDepartments] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [resourceBookingPopup, setResourceBookingPopup] = useState(null);
  const [uploadModal, setUploadModal] = useState(null); // { invoiceId, isCreditNote }
  const [conditions, setConditions] = useState([]);
  const [showConditions, setShowConditions] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [invoiceBookings, setInvoiceBookings] = useState([]);
  const [availableBookings, setAvailableBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [condForm, setCondForm] = useState({ required_status: 'paid_in_full', label: '' });
  const [payForm, setPayForm] = useState({ amount:'', payment_date: new Date().toISOString().split('T')[0], from_bank_account_id:'', to_bank_account_id:'', reference:'', notes:'', sage_reference:'', sender_reference:'' });
  const [bankTransactions, setBankTransactions] = useState([]);
  const [showBankTxForm, setShowBankTxForm] = useState(false);
  const [btForm, setBtForm] = useState({ bank_account_id:'', transaction_type:'principal', transaction_date: new Date().toISOString().split('T')[0], amount_gbp:'', foreign_currency_id:'', foreign_amount:'', exchange_rate:'', reference:'', notes:'' });

  // Locked-edit confirmation
  const [lockedEditConfirm, setLockedEditConfirm] = useState(null); // invoice to edit after confirm

  // Copy destination modal
  const [copyModal, setCopyModal] = useState(null); // { inv } when open
  const [copyDest, setCopyDest] = useState('this_project'); // 'this_project' | 'another_project'
  const [copyProjectTasks, setCopyProjectTasks] = useState([]); // all tasks in current project (for 'this_project')
  const [copyTargetTaskId, setCopyTargetTaskId] = useState('');
  const [copyOtherProjects, setCopyOtherProjects] = useState([]);
  const [copyOtherProjectId, setCopyOtherProjectId] = useState('');
  const [copyOtherTasks, setCopyOtherTasks] = useState([]);
  const [copyOtherTaskId, setCopyOtherTaskId] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyTasksLoading, setCopyTasksLoading] = useState(false);

  const { outMap, inMap } = useInvoiceStatuses();
  // Merge hardcoded base statuses with hook/server-managed ones. Union both so a
  // status defined only in the hook (or server config) still appears in the dropdown,
  // and the hook takes precedence for label/color where keys overlap.
  const baseOpts = getStatusOptions(isIn);
  const dynMap = isIn ? inMap : outMap;
  const statusOpts = { ...baseOpts };
  Object.entries(dynMap || {}).forEach(([k, v]) => { statusOpts[k] = { label: v.label, color: v.color }; });
  const defaultStatus = initialStatus || (isIn ? 'recv_do_not_pay' : 'to_invoice');

  const emptyForm = {
    issue_date: new Date().toISOString().split('T')[0], payment_terms:'14_days',
    pay_by_date:'', invoice_ref:'', invoice_number:'', status: defaultStatus, items:[],
    department_id:'', purchase_order_number:'', external_invoice_id:'', company_id:'', currency_id:'', bank_account_id:'',
    internal_notes:'', supplier_bank_account_id:'', supplier_company_id:'', supplier_contact_id:'',
    foreign_currency_id:'', foreign_currency_amount:'', exchange_rate:'', exchange_rate_gbp:'',
    bank_fee_amount:'', bank_fee_bank_account_id:'', bank_fee_nominal_code:'', bank_fee_notes:'',
    transaction_currency:'gbp', barter_bank_account_id:'', barter_unit_amount:'',
    cash_liability_percent:'', cash_liability_amount:'', auto_fee_amount:'', auto_fee_bank_account_id:'',
    billing_type:'one_off', repeat_count:3, repeat_interval:'monthly',
    subscription_min_term:12, subscription_min_term_unit:'months', subscription_rolling:false, subscription_end_date:'',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchInvoices = useCallback(async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const params = `project_id=${projectId}&direction=${direction}${taskParam}${showDeleted ? '&include_deleted=true' : ''}`;
      const { data } = await api.get(`/invoices?${params}`);
      setInvoices(data);
    } catch(e) {}
  }, [projectId, direction, showDeleted, taskId]);

  const fetchTemplates = async () => { try { const { data } = await api.get('/invoices/templates'); setTemplates(data); } catch(e) {} };
  // ── Accounts procedures for this project's client (migration 161) ──
  const [acctProcedures, setAcctProcedures] = useState([]);
  const [procPopup, setProcPopup] = useState(null); // full procedure for the pop-up viewer
  useEffect(() => {
    if (!isOpen || !projectId) { setAcctProcedures([]); return; }
    let cancelled = false;
    api.get(`/procedures?project_id=${projectId}&category=accounts`)
      .then(r => { if (!cancelled) setAcctProcedures(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAcctProcedures([]); });
    return () => { cancelled = true; };
  }, [isOpen, projectId]);
  const openProcPopup = async (procId) => {
    try { const { data } = await api.get(`/procedures/${procId}`); setProcPopup(data); } catch(e) {}
  };
  const fetchBanks = async () => { try { const { data } = await api.get('/bank-accounts?is_own=true'); setBankAccounts(data); } catch(e) {} };
  const fetchVatRates = async () => { try { const { data } = await api.get('/vat-rates'); setVatRates(data.filter(v => v.is_available)); } catch(e) {} };
  const fetchDepartments = async () => { try { const { data } = await api.get('/departments'); setDepartments(data); } catch(e) {} };
  const fetchCurrencies = async () => {
    try {
      const { data } = await api.get('/bank-accounts/currencies');
      setCurrencies(data);
      const def = data.find(c => c.is_default);
      if (def) setForm(p => p.currency_id ? p : { ...p, currency_id: String(def.id) });
    } catch(e) {}
  };
  const fetchBookings = async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const { data } = await api.get(`/resource-bookings?project_id=${projectId}${taskParam}`);
      setBookings(data);
    } catch(e) { setBookings([]); }
  };
  const fetchSupplierBanks = async (supplierId) => {
    if (!supplierId) { setSupplierBankAccounts([]); setSupplierContacts([]); return; }
    try {
      const [banksRes, contactsRes] = await Promise.all([
        api.get(`/bank-accounts?company_id=${supplierId}`),
        api.get(`/contacts?company_id=${supplierId}`),
      ]);
      setSupplierBankAccounts(banksRes.data);
      setSupplierContacts(contactsRes.data || []);
    } catch(e) { setSupplierBankAccounts([]); setSupplierContacts([]); }
  };

  const fetchBankTransactions = async (invoiceId) => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/bank-transactions`);
      setBankTransactions(data);
    } catch(e) { setBankTransactions([]); }
  };

  useEffect(() => {
    if (isOpen && projectId) {
      fetchInvoices(); fetchTemplates(); fetchBanks(); fetchVatRates();
      fetchDepartments(); fetchCurrencies(); fetchBookings();
      setCreating(false); setEditing(false); setSelectedInvoice(null); setSelectedBookingId('');
    }
  }, [isOpen, projectId, fetchInvoices]); // eslint-disable-line

  // ── Copy/New launcher (used by the Bookkeeper ledger) ──────────────────────
  // When the modal is opened with a launchAction, jump straight into the
  // pre-populated, editable create form (or the copy-to-different-client
  // picker) instead of the normal invoice list. Runs once per open.
  const launchedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { launchedRef.current = false; return; }
    if (!launchAction || launchedRef.current || !projectId) return;
    // For blank/clone we want a sensible default currency — wait for it to load.
    if ((launchAction.type === 'blank' || launchAction.type === 'clone') && currencies.length === 0) return;
    launchedRef.current = true;
    (async () => {
      const def = currencies.find(c => c.is_default);
      const defCur = def ? String(def.id) : '';
      if (launchAction.type === 'blank') {
        setSelectedInvoice(null); setEditing(false);
        setForm({ ...emptyForm, currency_id: defCur });
        setCreating(true);
      } else if (launchAction.type === 'clone' && launchAction.invoiceId) {
        try {
          const { data: inv } = await api.get(`/invoices/${launchAction.invoiceId}`);
          const f = buildFormFromInvoice(inv, true);
          if (!f.currency_id) f.currency_id = defCur;
          setSelectedInvoice(null); setEditing(false);
          setForm(f); setCreating(true);
          if (inv.supplier_company_id) {
            setSupplierCompanyName(inv.supplier_company_name || '');
            fetchSupplierBanks(inv.supplier_company_id);
          }
        } catch(e) { toast({ title: 'Could not load invoice to copy', status: 'error', duration: 2500 }); }
      } else if (launchAction.type === 'clone_other_client' && launchAction.invoiceId) {
        openCopyModal(launchAction.invoiceId, 'another_project');
      }
    })();
  }, [isOpen, launchAction, projectId, currencies]); // eslint-disable-line

  const addItemFromTemplate = async (tmpl) => {
    setForm(p => ({ ...p, items: [...p.items, { item_code: tmpl.item_code, nominal_code: tmpl.nominal_code, description: tmpl.description, custom_description:'', quantity:1, net_price: tmpl.default_net_price, vat_rate: tmpl.default_vat_rate }]}));
    try {
      const { data: reqs } = await api.get(`/resources/product-requirements/${tmpl.id}`);
      if (reqs?.length > 0) {
        setResourceBookingPopup({ template: tmpl, requirements: reqs, dates: reqs.reduce((acc, r) => ({ ...acc, [r.id]: { date: '', time: '', skip: false } }), {}) });
      }
    } catch(e) {}
  };
  const addBlankItem = () => setForm(p => ({ ...p, items: [...p.items, { item_code:'', nominal_code:'', description:'', custom_description:'', quantity:1, net_price:0, vat_rate:20 }]}));
  const addNoteLine = () => setForm(p => ({ ...p, items: [...p.items, { is_note:true, item_code:'', nominal_code:'', description:'', custom_description:'', quantity:0, net_price:0, vat_rate:0 }]}));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_,i) => i !== idx) }));
  const updateItem = (idx, field, val) => setForm(p => ({ ...p, items: p.items.map((item,i) => i === idx ? { ...item, [field]: val } : item) }));

  const confirmResourceBookings = async () => {
    if (!resourceBookingPopup) return;
    for (const req of resourceBookingPopup.requirements) {
      const entry = resourceBookingPopup.dates[req.id];
      if (!entry || entry.skip) continue;
      try {
        await api.post('/resource-bookings', { project_id: projectId, task_id: taskId||null, title: resourceBookingPopup.template.description, start_date: entry.date||null, start_time: entry.time||null, end_date: entry.date||null, resource_ids: req.resource_id ? [req.resource_id] : [], status: 'tentative' });
      } catch(e) {}
    }
    setResourceBookingPopup(null);
    toast({ title: 'Resource bookings created', status: 'success', duration: 2000 });
  };

  const calcTotal = (items) => {
    let net=0, vat=0;
    (items||[]).forEach(i => { if (i.is_note) return; const ln=(parseFloat(i.quantity)||1)*(parseFloat(i.net_price)||0); const lv=ln*((parseFloat(i.vat_rate)||0)/100); net+=ln; vat+=lv; });
    return { net, vat, gross: net+vat };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let invoiceId;
      if (editing && selectedInvoice) {
        await api.put(`/invoices/${selectedInvoice.id}`, { ...form, items: form.items.map(({ _showNote, ...i }) => i) });
        invoiceId = selectedInvoice.id;
        // If status changed to recv_credited, prompt credit note upload
        if (form.status === 'recv_credited' && form.status !== selectedInvoice.status) {
          setUploadModal({ invoiceId, isCreditNote: true });
        }
        toast({ title:'Invoice updated', status:'success', duration:1500 });
      } else {
        if (!isIn && form.items.length === 0) { toast({ title:'Add at least one item', status:'warning', duration:2000 }); setSaving(false); return; }
        const { data } = await api.post('/invoices', { ...form, items: form.items.map(({ _showNote, ...i }) => i), project_id: projectId, task_id: taskId||null, direction });
        invoiceId = data.id;

        // Save internal note if provided
        if (form.internal_notes?.trim()) {
          try {
            await api.post('/notes', {
              project_id: projectId, task_id: taskId||null,
              subject: `Invoice note: ${data.invoice_ref || data.invoice_number || invoiceId}`,
              body: form.internal_notes, entity_type: isIn ? 'supplier' : 'client',
            });
          } catch(e) {}
        }

        // Link booking
        if (selectedBookingId && invoiceId) {
          try {
            await api.put(`/resource-bookings/${selectedBookingId}/invoice`, { invoice_id: invoiceId });
            const booking = bookings.find(b => b.id === selectedBookingId);
            if (booking?.status === 'tentative') await api.put(`/resource-bookings/${selectedBookingId}`, { status: 'confirmed' });
          } catch(e) {}
        }

        // After create, prompt for invoice document upload (for IN invoices)
        await fetchInvoices(); setCreating(false); setEditing(false); setSelectedInvoice(null); setForm(emptyForm);
        if (isIn) {
          setUploadModal({ invoiceId, isCreditNote: form.status === 'recv_credited', invoiceRef: data.invoice_ref || data.invoice_number });
        } else {
          toast({ title:'Invoice created', status:'success', duration:1500 });
        }
        return;
      }
      await fetchInvoices(); setCreating(false); setEditing(false); setSelectedInvoice(null); setForm(emptyForm);
    } catch(e) {
      const msg = e.response?.data?.error || e.message || 'Failed';
      toast({ title: msg, status: 'error', duration: 4000 });
    }
    finally { setSaving(false); }
  };

  const viewInvoice = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSelectedInvoice(data);
      setEditing(false); setShowPayment(false); setShowConditions(false); setShowResources(false);
      setInvoiceBookings([]); setAvailableBookings([]);
      setShowBankTxForm(false);
      setBankTransactions([]);
      // Fetch task conditions and bank transactions for this invoice
      try { const { data: conds } = await api.get(`/invoices/${id}/conditions`); setConditions(conds || []); } catch(e) { setConditions([]); }
      try { const { data: bts } = await api.get(`/invoices/${id}/bank-transactions`); setBankTransactions(bts || []); } catch(e) {}
    } catch(e) {}
  };

  // Build the form state from an invoice object (shared by copy and edit)
  const buildFormFromInvoice = (inv, isNewCopy = false) => {
    const today = new Date().toISOString().split('T')[0];
    return {
      issue_date: isNewCopy ? today : (inv.issue_date?.split('T')[0] || today),
      payment_terms: inv.payment_terms || '14_days',
      pay_by_date: isNewCopy ? '' : (inv.pay_by_date?.split('T')[0] || ''),
      invoice_ref: isNewCopy ? (inv.invoice_ref ? `COPY — ${inv.invoice_ref}` : '') : (inv.invoice_ref || ''),
      invoice_number: isNewCopy ? '' : (inv.invoice_number || ''),
      status: isNewCopy ? (isIn ? 'recv_do_not_pay' : 'to_invoice') : (inv.status || defaultStatus),
      department_id: inv.department_id || '',
      purchase_order_number: isNewCopy ? '' : (inv.purchase_order_number || ''),
      external_invoice_id: isNewCopy ? '' : (inv.external_invoice_id || ''),
      company_id: inv.company_id || '',
      currency_id: inv.currency_id || '',
      bank_account_id: inv.bank_account_id || '',
      internal_notes: isNewCopy
        ? (inv.internal_notes ? `Copied from ${inv.invoice_number || inv.invoice_ref || '#' + inv.id}` : '')
        : (inv.internal_notes || ''),
      supplier_bank_account_id: inv.supplier_bank_account_id || '',
      supplier_company_id: inv.supplier_company_id || '',
      supplier_contact_id: inv.supplier_contact_id || '',
      foreign_currency_id: inv.foreign_currency_id || '',
      foreign_currency_amount: inv.foreign_currency_amount || '',
      exchange_rate: inv.exchange_rate || '',
      exchange_rate_gbp: inv.exchange_rate_gbp || '',
      bank_fee_amount: isNewCopy ? '' : (inv.bank_fee_amount || ''),
      bank_fee_bank_account_id: isNewCopy ? '' : (inv.bank_fee_bank_account_id || ''),
      bank_fee_nominal_code: isNewCopy ? '' : (inv.bank_fee_nominal_code || ''),
      bank_fee_notes: isNewCopy ? '' : (inv.bank_fee_notes || ''),
      billing_type: 'one_off',
      repeat_count: 3, repeat_interval: 'monthly',
      subscription_min_term: 12, subscription_min_term_unit: 'months',
      subscription_rolling: false, subscription_end_date: '',
      items: (inv.items || []).map(i => ({
        item_code: i.item_code || '',
        nominal_code: i.nominal_code || '',
        description: i.description || '',
        custom_description: i.custom_description || '',
        quantity: i.quantity,
        net_price: i.net_price,
        vat_rate: i.vat_rate,
        is_note: !!i.is_note,
        _showNote: !!(i.custom_description),
      })),
    };
  };

  // Open the "Copy as New" destination picker
  const openCopyModal = async (invId, defaultDest = 'this_project') => {
    try {
      const { data: inv } = await api.get(`/invoices/${invId}`);
      setCopyModal({ inv });
      setCopyDest(defaultDest);
      // Default the "within this project" selection to the invoice's current task
      setCopyTargetTaskId(inv.task_id || taskId || '');
      setCopyOtherProjectId('');
      setCopyOtherTaskId('');
      setCopyOtherProjects([]);
      setCopyOtherTasks([]);
      // Load ALL tasks in this project, using the invoice's own project UUID as the
      // source of truth. Always include the invoice's current task so it is selectable
      // even if the broader list can't be loaded.
      const srcProjectId = inv.project_id || projectId;
      const currentTaskOpt = inv.task_id ? [{ id: inv.task_id, task_title: inv.task_title || 'Current task' }] : [];
      try {
        const { data: tData } = await api.get(`/tasks?project_id=${encodeURIComponent(srcProjectId)}`);
        let tasks = Array.isArray(tData) ? tData : [];
        if (inv.task_id && !tasks.some(t => String(t.id) === String(inv.task_id))) {
          tasks = [...currentTaskOpt, ...tasks];
        }
        setCopyProjectTasks(tasks);
      } catch(e) {
        setCopyProjectTasks(currentTaskOpt);
        const code = e?.response?.status;
        toast({
          title: code === 403 ? 'No permission to list this project\u2019s tasks' : 'Could not load the full task list for this project',
          status: 'warning', duration: 3000,
        });
      }
    } catch(e) {
      toast({ title: 'Could not load invoice for copying', status: 'error', duration: 2000 });
    }
  };

  // Execute copy to chosen destination
  const executeCopy = async () => {
    if (!copyModal?.inv) return;
    setCopyLoading(true);
    try {
      const inv = copyModal.inv;
      const formData = buildFormFromInvoice(inv, true);
      const srcProjectId = inv.project_id || projectId;
      let destProjectId = srcProjectId;
      let destTaskId = taskId;

      if (copyDest === 'this_project') {
        // Assign to any task within this project ('' = project-level, no task)
        destProjectId = srcProjectId;
        destTaskId = copyTargetTaskId || null;
      } else if (copyDest === 'another_project') {
        if (!copyOtherProjectId) { toast({ title: 'Choose a project', status: 'warning', duration: 2000 }); setCopyLoading(false); return; }
        destProjectId = copyOtherProjectId;
        destTaskId = copyOtherTaskId || null; // task optional — project-level allowed
      }

      // Create the copy invoice via API
      const { data: newInv } = await api.post('/invoices', {
        ...formData,
        items: formData.items.map(({ _showNote, ...i }) => i),
        project_id: destProjectId,
        task_id: destTaskId || null,
        direction,
      });

      setCopyModal(null);

      // If copying to current project/task, load it in edit mode
      if (destProjectId === projectId) {
        await fetchInvoices();
        const { data: freshInv } = await api.get(`/invoices/${newInv.id}`);
        setSelectedInvoice(freshInv);
        startEditDirect(freshInv);
      } else {
        toast({ title: 'Invoice copied to the selected project', status: 'success', duration: 3000 });
      }
    } catch(e) {
      toast({ title: e.response?.data?.error || 'Copy failed', status: 'error', duration: 3000 });
    }
    setCopyLoading(false);
  };

  const copyInvoice = (invId) => openCopyModal(invId);

  // Start editing — with locked-invoice guard
  const requestEdit = (inv) => {
    const invoice = inv || selectedInvoice;
    if (!invoice) return;
    const isLocked = ['sent_for_payment','invoiced_within_terms','paid_in_part','paid_in_full',
      'overdue','very_overdue','written_off','send_for_debt_collection','receipt',
      'recv_pay_within_terms','recv_overdue','recv_receipt','recv_paid_in_full'].includes(invoice.status);
    if (isLocked) {
      setLockedEditConfirm(invoice);
    } else {
      startEditDirect(invoice);
    }
  };

  // Actually load into edit mode (no lock check)
  const startEditDirect = (inv) => {
    const invoice = inv || selectedInvoice;
    if (!invoice) return;
    setForm(buildFormFromInvoice(invoice, false));
    if (invoice.supplier_company_id) {
      setSupplierCompanyName(invoice.supplier_company_name || '');
      fetchSupplierBanks(invoice.supplier_company_id);
    } else {
      setSupplierCompanyName('');
    }
    setEditing(true);
    setSelectedInvoice(invoice);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/invoices/${id}`, { data: { reason: deleteReason } }); toast({ title:'Deleted', status:'info', duration:1500 }); await fetchInvoices(); setSelectedInvoice(null); setShowDeleteConfirm(null); setDeleteReason(''); } catch(e) { toast({ title:'Failed', status:'error', duration:2000 }); }
  };
  const handleUndelete = async (id) => {
    try { await api.post(`/invoices/${id}/undelete`, { creation_reason:'Restored' }); toast({ title:'Restored', status:'success', duration:2000 }); await fetchInvoices(); setSelectedInvoice(null); } catch(e) { toast({ title:'Failed', status:'error', duration:2000 }); }
  };
  const handlePayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast({ title:'Enter amount', status:'warning', duration:2000 }); return; }
    setSaving(true);
    try { await api.post(`/invoices/${selectedInvoice.id}/payments`, payForm); toast({ title:'Payment recorded', status:'success', duration:1500 }); await viewInvoice(selectedInvoice.id); await fetchInvoices(); setShowPayment(false); setPayForm({ amount:'', payment_date: new Date().toISOString().split('T')[0], from_bank_account_id:'', to_bank_account_id:'', reference:'', notes:'', sage_reference:'', sender_reference:'' }); } catch(e) { toast({ title:'Failed', description: e?.response?.data?.error || '', status:'error', duration:2500 }); }
    finally { setSaving(false); }
  };
  const handleStatusChange = async (id, status) => {
    try { await api.put(`/invoices/${id}/status`, { status }); await fetchInvoices(); } catch(e) {}
  };

  const totals = calcTotal(form.items);

  // ── Edit/create as a real page ────────────────────────────────────────────
  // The edit/create form is shown as a full-page surface (not a modal), and we
  // reflect it in the URL (#invoice-edit) so it reads as a page and the browser
  // Back button closes it. Centralised here so every open/close path stays in
  // sync automatically.
  const exitForm = () => { setCreating(false); setEditing(false); setForm(emptyForm); };
  const formOpen = isOpen && (creating || editing);
  useEffect(() => {
    if (!formOpen) return;
    const MARK = '#invoice-edit';
    if (window.location.hash !== MARK) {
      window.history.pushState({ invoiceEditPage: true }, '', MARK);
    }
    const onPop = () => { setCreating(false); setEditing(false); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.location.hash === MARK) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };
    // eslint-disable-next-line
  }, [formOpen]);

  // ── Form render ─────────────────────────────────────────────────────────────
  const renderForm = () => (
    <Box bg={isIn ? 'orange.50' : 'green.50'} p={3} borderRadius="md" mb={4} border="1px" borderColor={isIn ? 'orange.200' : 'green.200'}>
      <VStack spacing={3}>
        {/* Billing Type — One-off / Repeat / Subscription */}
        {!isIn && (
          <Box w="100%" bg="white" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
            <HStack spacing={4} mb={form.billing_type !== 'one_off' ? 3 : 0}>
              {['one_off','repeat','subscription'].map(bt => (
                <Box key={bt} px={3} py={1.5} borderRadius="full" cursor="pointer" fontSize="12px" fontWeight="600"
                  bg={form.billing_type === bt ? 'teal.500' : 'gray.100'}
                  color={form.billing_type === bt ? 'white' : 'gray.600'}
                  onClick={() => setForm(p => ({ ...p, billing_type: bt }))}>
                  {bt === 'one_off' ? 'One-off' : bt === 'repeat' ? '🔁 Repeat' : '♾️ Subscription'}
                </Box>
              ))}
            </HStack>
            {form.billing_type === 'repeat' && (
              <HStack spacing={3} flexWrap="wrap">
                <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Repeat Count</FormLabel>
                  <Input size="xs" type="number" min={1} max={60} value={form.repeat_count}
                    onChange={e => setForm(p => ({ ...p, repeat_count: parseInt(e.target.value)||1 }))} /></FormControl>
                <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Interval</FormLabel>
                  <Select size="xs" value={form.repeat_interval} onChange={e => setForm(p => ({ ...p, repeat_interval: e.target.value }))}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biannual">Every 6 months</option>
                    <option value="yearly">Yearly</option>
                  </Select></FormControl>
                <Box flex={3} alignSelf="flex-end" pb={1}>
                  <Text fontSize="10px" color="gray.500">
                    {form.repeat_count} invoice{form.repeat_count > 1 ? 's' : ''} will be created automatically as Future Contractual Invoices
                  </Text>
                </Box>
              </HStack>
            )}
            {form.billing_type === 'subscription' && (
              <VStack spacing={2} align="stretch">
                <HStack spacing={3}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Minimum Term</FormLabel>
                    <Input size="xs" type="number" min={1} value={form.subscription_min_term}
                      onChange={e => setForm(p => ({ ...p, subscription_min_term: parseInt(e.target.value)||1 }))} /></FormControl>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Unit</FormLabel>
                    <Select size="xs" value={form.subscription_min_term_unit} onChange={e => setForm(p => ({ ...p, subscription_min_term_unit: e.target.value }))}>
                      <option value="months">Months</option>
                      <option value="weeks">Weeks</option>
                      <option value="years">Years</option>
                    </Select></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Interval</FormLabel>
                    <Select size="xs" value={form.repeat_interval} onChange={e => setForm(p => ({ ...p, repeat_interval: e.target.value }))}>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="biannual">Every 6 months</option>
                      <option value="yearly">Yearly</option>
                    </Select></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>End Date</FormLabel>
                    <Input size="xs" type="date" value={form.subscription_end_date}
                      onChange={e => setForm(p => ({ ...p, subscription_end_date: e.target.value }))} /></FormControl>
                </HStack>
                <HStack>
                  <Switch size="sm" colorScheme="teal" isChecked={!!form.subscription_rolling}
                    onChange={e => setForm(p => ({ ...p, subscription_rolling: e.target.checked }))} />
                  <Text fontSize="11px" color="gray.600">Rolling (continues monthly after minimum term — auto-billed until cancelled)</Text>
                </HStack>
                <Text fontSize="10px" color="teal.600">
                  {form.subscription_min_term} {form.subscription_min_term_unit} minimum term — invoices created as Future Contractual Invoices
                  {form.subscription_rolling ? ', then rolling' : ''}
                </Text>
              </VStack>
            )}
          </Box>
        )}
        {/* Row 1: Date, Terms, Status */}
        <HStack w="100%" spacing={2}>
          {!isIn && <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Invoice #</FormLabel>
            <Input size="xs" value={form.invoice_number} onChange={e => setForm(p => ({ ...p, invoice_number: e.target.value }))} placeholder="Auto-generated" /></FormControl>}
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Issue Date</FormLabel>
            <Input size="xs" type="date" value={form.issue_date} onChange={e => setForm(p => ({ ...p, issue_date: e.target.value }))} /></FormControl>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Terms</FormLabel>
            <Select size="xs" value={form.payment_terms} onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))}>
              <option value="now_due">Now Due</option><option value="14_days">14 Days</option>
              <option value="30_days">30 Days</option><option value="60_days">60 Days</option></Select></FormControl>
          <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Status</FormLabel>
            <Select size="xs" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
              {Object.entries(statusOpts).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select></FormControl>
        </HStack>

        {/* IN: credit note warning */}
        {isIn && form.status === 'recv_credited' && (
          <Box bg="teal.50" p={2} borderRadius="md" border="1px" borderColor="teal.200" w="100%">
            <Text fontSize="10px" color="teal.700" fontWeight="600">
              ℹ️ You will be prompted to upload the credit note and add a reason after saving.
            </Text>
          </Box>
        )}

        {/* Received-invoice document upload (available once the invoice exists) */}
        {isIn && editing && selectedInvoice?.id && (
          <Box w="100%" bg="orange.50" p={3} borderRadius="md" border="1px" borderColor="orange.200">
            <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="wide" mb={1}>
              Received Invoice Document
            </Text>
            <Text fontSize="10px" color="gray.500" mb={2}>Attach the supplier's invoice (PDF, spreadsheet, Word, image…). It's versioned and appears in this task's Documents.</Text>
            <InvoiceDocumentCell invoice={selectedInvoice} onChanged={fetchInvoices} />
          </Box>
        )}
        {isIn && !editing && (
          <Box w="100%" bg="gray.50" p={2.5} borderRadius="md" border="1px dashed" borderColor="gray.300">
            <Text fontSize="10px" color="gray.500">
              📎 Save the invoice first (<b>Record Invoice Received</b>), then the drag-and-drop document upload appears here so you can attach the received invoice.
            </Text>
          </Box>
        )}

        {/* Row 2: Ref / Pay By */}
        {isIn && <HStack w="100%" spacing={2}>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Supplier Ref / Invoice #</FormLabel>
            <Input size="xs" value={form.invoice_ref} onChange={e => setForm(p => ({ ...p, invoice_ref: e.target.value }))} /></FormControl>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Pay By</FormLabel>
            <Input size="xs" type="date" value={form.pay_by_date} onChange={e => setForm(p => ({ ...p, pay_by_date: e.target.value }))} /></FormControl>
        </HStack>}

        {/* Row 3: Invoice ID, PO, Department, Currency */}
        <HStack w="100%" spacing={2}>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Invoice ID</FormLabel>
            <Input size="xs" value={form.external_invoice_id} onChange={e => setForm(p => ({ ...p, external_invoice_id: e.target.value }))} placeholder="External ref" /></FormControl>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>PO Number</FormLabel>
            <Input size="xs" value={form.purchase_order_number} onChange={e => setForm(p => ({ ...p, purchase_order_number: e.target.value }))} /></FormControl>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Department</FormLabel>
            <Select size="xs" value={form.department_id} onChange={e => setForm(p => ({ ...p, department_id: e.target.value }))}>
              <option value="">None</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select></FormControl>
          <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Currency</FormLabel>
            <Select size="xs" value={form.currency_id} onChange={e => setForm(p => ({ ...p, currency_id: e.target.value }))}>
              <option value="">Default</option>
              {currencies.map(c => <option key={c.id} value={c.id}>{c.code} — {c.symbol}</option>)}
            </Select></FormControl>
        </HStack>

        {/* Bank accounts */}
        {!isIn && (
          <FormControl w="100%"><FormLabel fontSize="xs" mb={0.5}>Receive Into Account</FormLabel>
            <Select size="xs" value={form.bank_account_id} onChange={e => setForm(p => ({ ...p, bank_account_id: e.target.value }))}>
              <option value="">None</option>
              {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name || b.bank_name} {b.currency_code ? `(${b.currency_code})` : ''}</option>)}
            </Select></FormControl>
        )}
        {isIn && (
          <Box w="100%" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
              Supplier / Payee
            </Text>

            {/* Step 1: Company picker */}
            <FormControl mb={2}>
              <FormLabel fontSize="xs" mb={0.5}>Company <Text as="span" color="gray.400" fontWeight="400">(required to select bank account)</Text></FormLabel>
              <CompanySearch
                value={supplierCompanyName}
                companyId={form.supplier_company_id || undefined}
                onSelect={(company) => {
                  const id = company?.id;
                  setForm(p => ({ ...p, supplier_company_id: id || '', supplier_bank_account_id: '', supplier_contact_id: '' }));
                  setSupplierCompanyName(company?.name || '');
                  setSupplierContacts([]);
                  setSupplierBankAccounts([]);
                  if (id) fetchSupplierBanks(id);
                }}
              />
            </FormControl>

            {/* Step 2: Contact (optional, only once company chosen) */}
            {form.supplier_company_id && supplierContacts.length > 0 && (
              <FormControl mb={2}>
                <FormLabel fontSize="xs" mb={0.5}>Contact <Text as="span" color="gray.400" fontWeight="400">(optional)</Text></FormLabel>
                <Select size="xs" value={form.supplier_contact_id} onChange={e => setForm(p => ({ ...p, supplier_contact_id: e.target.value }))}>
                  <option value="">— No specific contact —</option>
                  {supplierContacts.map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}</option>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Step 3: Bank account (only once company chosen) */}
            {form.supplier_company_id && (
              <>
                <FormControl mb={supplierBankAccounts.length === 0 ? 0 : 1}>
                  <FormLabel fontSize="xs" mb={0.5}>Bank Account to Pay</FormLabel>
                  {supplierBankAccounts.length > 0 ? (
                    <Select size="xs" value={form.supplier_bank_account_id}
                      onChange={e => setForm(p => ({ ...p, supplier_bank_account_id: e.target.value }))}>
                      <option value="">— Select bank account —</option>
                      {supplierBankAccounts.map(b => (
                        <option key={b.id} value={b.id}>
                          {b.account_name || b.bank_name}
                          {b.account_number ? ` — ${b.account_number}` : ''}
                          {b.sort_code ? ` (${b.sort_code})` : ''}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text fontSize="11px" color="gray.400" fontStyle="italic">No bank accounts on file for this company</Text>
                  )}
                </FormControl>
                <Box mt={2}>
                  <InlineAddBankAccount
                    companyId={form.supplier_company_id}
                    onCreated={b => {
                      setSupplierBankAccounts(p => [...p, b]);
                      setForm(p => ({ ...p, supplier_bank_account_id: String(b.id) }));
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Resource booking link (OUT only) */}
        {!isIn && bookings.length > 0 && (
          <FormControl>
            <FormLabel fontSize="xs" mb={0.5}>Link to Resource Booking</FormLabel>
            <Select size="xs" value={selectedBookingId} onChange={e => setSelectedBookingId(e.target.value)}>
              <option value="">— No booking —</option>
              {bookings.map(b => { const dt = b.start_date ? new Date(b.start_date.split('T')[0]+'T12:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short'}) : 'No date'; const res = (b.resources||[]).map(r=>r.resource_name||r.resource_code).join(', '); return <option key={b.id} value={b.id}>[{b.status}] {dt} — {b.title||'(untitled)'}{res?` (${res})`:''}</option>; })}
            </Select>
          </FormControl>
        )}

        <Divider />

        {/* Templates (OUT only) */}
        {!isIn && (
          <FormControl><FormLabel fontSize="xs" mb={0.5}>Add from Template</FormLabel>
            <Select size="xs" placeholder="Select..." onChange={e => { const t = templates.find(t => t.id === e.target.value); if (t) addItemFromTemplate(t); e.target.value=''; }}>
              {templates.map(t => <option key={t.id} value={t.id}>{t.item_code} — {t.description}</option>)}
            </Select></FormControl>
        )}
        <HStack>
          <Button size="xs" variant="ghost" leftIcon={<FiPlus />} onClick={addBlankItem}>Add {isIn ? 'Line Item' : 'Blank Item'}</Button>
          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiMessageSquare />} onClick={addNoteLine}>Add Note Line</Button>
        </HStack>

        {/* Line items table */}
        {form.items.length > 0 && (
          <Box w="100%" overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead><Tr>
                <Th fontSize="9px" px={1} w="70px">Code</Th>
                <Th fontSize="9px" px={1}>Description</Th>
                <Th fontSize="9px" px={1} w="45px">Qty</Th>
                <Th fontSize="9px" px={1} w="80px">Net £ (edit)</Th>
                <Th fontSize="9px" px={1} w="70px">VAT Rate</Th>
                <Th fontSize="9px" px={1} w="65px" isNumeric>Line Net</Th>
                <Th fontSize="9px" px={1} w="55px" isNumeric>VAT</Th>
                <Th fontSize="9px" px={1} w="65px" isNumeric>Gross</Th>
                <Th w="25px"></Th>
              </Tr></Thead>
              <Tbody>
                {form.items.map((item, idx) => {
                  const qty=parseFloat(item.quantity)||1; const net=parseFloat(item.net_price)||0;
                  const lineNet=qty*net; const vatPct=parseFloat(item.vat_rate)||0;
                  const lineVat=lineNet*(vatPct/100);
                  if (item.is_note) {
                    return (
                      <Tr key={idx} bg="blue.50">
                        <Td px={1} colSpan={8} py={1}>
                          <HStack align="flex-start" spacing={2}>
                            <Text fontSize="9px" fontWeight="700" color="blue.600" mt={2} flexShrink={0}>NOTE</Text>
                            <Textarea size="xs" rows={2} resize="vertical" value={item.description||''}
                              onChange={e=>updateItem(idx,'description',e.target.value)}
                              placeholder="Note line (printed on the invoice, no charge)…"
                              bg="white" borderColor="blue.200" _focus={{ borderColor:'blue.400' }} />
                          </HStack>
                        </Td>
                        <Td px={0}>
                          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={()=>removeItem(idx)} aria-label="Remove note" />
                        </Td>
                      </Tr>
                    );
                  }
                  return <React.Fragment key={idx}>
                    <Tr>
                      <Td px={1}><Input size="xs" value={item.item_code||''} onChange={e=>updateItem(idx,'item_code',e.target.value)} placeholder="Code" /></Td>
                      <Td px={1} minW="160px"><Textarea size="xs" rows={1} value={item.description} onChange={e=>updateItem(idx,'description',e.target.value)} resize="none" /></Td>
                      <Td px={1}><Input size="xs" type="number" w="50px" value={item.quantity} onChange={e=>updateItem(idx,'quantity',e.target.value)} /></Td>
                      <Td px={1}><Input size="xs" type="number" step="0.01" w="75px" value={item.net_price} onChange={e=>updateItem(idx,'net_price',e.target.value)} /></Td>
                      <Td px={1}>
                        <Select size="xs" value={item.vat_rate} onChange={e=>updateItem(idx,'vat_rate',e.target.value)}>
                          {vatRates.length>0 ? vatRates.map(v=><option key={v.id} value={v.rate}>{v.code} ({v.rate}%)</option>) : <><option value="20">S (20%)</option><option value="5">R (5%)</option><option value="0">Z (0%)</option></>}
                        </Select>
                      </Td>
                      <Td px={1} isNumeric fontSize="9px">£{lineNet.toFixed(2)}</Td>
                      <Td px={1} isNumeric fontSize="9px" color="gray.500">£{lineVat.toFixed(2)}</Td>
                      <Td px={1} isNumeric fontSize="10px" fontWeight="600">£{(lineNet+lineVat).toFixed(2)}</Td>
                      <Td px={0}>
                        <HStack spacing={0}>
                          <Tooltip label={item.custom_description ? 'Edit note' : 'Add note'}>
                            <IconButton
                              icon={<FiMessageSquare />}
                              size="xs" variant="ghost"
                              colorScheme={item.custom_description ? 'blue' : 'gray'}
                              opacity={item.custom_description ? 1 : 0.4}
                              onClick={() => updateItem(idx, '_showNote', !item._showNote)}
                              aria-label="Toggle note"
                            />
                          </Tooltip>
                          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={()=>removeItem(idx)} />
                        </HStack>
                      </Td>
                    </Tr>
                    {(item._showNote || item.custom_description) && (
                      <Tr bg="blue.50">
                        <Td px={1} colSpan={9} py={1}>
                          <Textarea
                            size="xs" rows={2} resize="vertical"
                            value={item.custom_description || ''}
                            onChange={e => updateItem(idx, 'custom_description', e.target.value)}
                            placeholder="Note line (printed beneath this item on the invoice)…"
                            bg="white" borderColor="blue.200"
                            _focus={{ borderColor: 'blue.400' }}
                          />
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>;
                })}
              </Tbody>
            </Table>
          </Box>
        )}
        <Flex w="100%" justify="flex-end" fontSize="12px" fontWeight="700" gap={4}>
          <Text>Net: £{totals.net.toFixed(2)}</Text>
          <Text>VAT: £{totals.vat.toFixed(2)}</Text>
          <Text color={isIn ? 'orange.700' : 'brand.700'}>Total: £{totals.gross.toFixed(2)}</Text>
        </Flex>

        {/* ── Foreign Currency & Bank Fees ── */}
        {isIn && (
          <Box w="100%" bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={3}>
            <Text fontSize="10px" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="wide" mb={2}>
              Foreign Currency & Bank Fees
            </Text>

            {/* Foreign currency */}
            <HStack w="100%" spacing={2} align="flex-end" mb={2}>
              <FormControl maxW="140px">
                <FormLabel fontSize="xs" mb={0.5}>Invoice Currency</FormLabel>
                <Select size="xs" value={form.foreign_currency_id} onChange={e => setForm(p => ({ ...p, foreign_currency_id: e.target.value }))}>
                  <option value="">GBP (default)</option>
                  {currencies.filter(c => c.code !== 'GBP').map(c => (
                    <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                  ))}
                </Select>
              </FormControl>
              {form.foreign_currency_id && (
                <>
                  <FormControl maxW="130px">
                    <FormLabel fontSize="xs" mb={0.5}>Amount in Foreign Currency</FormLabel>
                    <Input size="xs" type="number" step="0.01" placeholder="0.00"
                      value={form.foreign_currency_amount}
                      onChange={e => {
                        const fa = e.target.value;
                        setForm(p => {
                          const gbp = p.exchange_rate_gbp && fa ? (parseFloat(fa) * parseFloat(p.exchange_rate_gbp)).toFixed(2) : p.foreign_currency_amount;
                          return { ...p, foreign_currency_amount: fa };
                        });
                      }} />
                  </FormControl>
                  <FormControl maxW="120px">
                    <FormLabel fontSize="xs" mb={0.5}>Rate: 1 foreign = £</FormLabel>
                    <Input size="xs" type="number" step="0.000001" placeholder="e.g. 0.789"
                      value={form.exchange_rate_gbp}
                      onChange={e => {
                        const rate = e.target.value;
                        setForm(p => ({
                          ...p, exchange_rate_gbp: rate,
                          exchange_rate: rate && parseFloat(rate) > 0 ? (1 / parseFloat(rate)).toFixed(6) : '',
                        }));
                      }} />
                  </FormControl>
                  <FormControl maxW="120px">
                    <FormLabel fontSize="xs" mb={0.5}>Rate: 1 GBP = foreign</FormLabel>
                    <Input size="xs" type="number" step="0.000001" placeholder="e.g. 1.267"
                      value={form.exchange_rate}
                      onChange={e => {
                        const rate = e.target.value;
                        setForm(p => ({
                          ...p, exchange_rate: rate,
                          exchange_rate_gbp: rate && parseFloat(rate) > 0 ? (1 / parseFloat(rate)).toFixed(6) : '',
                        }));
                      }} />
                  </FormControl>
                  {form.foreign_currency_amount && form.exchange_rate_gbp && (
                    <Box>
                      <Text fontSize="9px" color="gray.500" mb={0.5}>GBP Equivalent</Text>
                      <Text fontSize="12px" fontWeight="700" color="blue.700">
                        £{(parseFloat(form.foreign_currency_amount || 0) * parseFloat(form.exchange_rate_gbp || 0)).toFixed(2)}
                      </Text>
                    </Box>
                  )}
                </>
              )}
            </HStack>

            {/* Bank fee */}
            <HStack w="100%" spacing={2} align="flex-end" flexWrap="wrap">
              <FormControl maxW="120px">
                <FormLabel fontSize="xs" mb={0.5}>Bank Fee (£)</FormLabel>
                <Input size="xs" type="number" step="0.01" placeholder="0.00"
                  value={form.bank_fee_amount}
                  onChange={e => setForm(p => ({ ...p, bank_fee_amount: e.target.value }))} />
              </FormControl>
              {form.bank_fee_amount && parseFloat(form.bank_fee_amount) > 0 && (
                <>
                  <FormControl maxW="200px">
                    <FormLabel fontSize="xs" mb={0.5}>Fee charged to account</FormLabel>
                    <Select size="xs" value={form.bank_fee_bank_account_id}
                      onChange={e => setForm(p => ({ ...p, bank_fee_bank_account_id: e.target.value }))}>
                      <option value="">— Select bank account —</option>
                      {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name || b.bank_name}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl maxW="130px">
                    <FormLabel fontSize="xs" mb={0.5}>Fee Nominal Code</FormLabel>
                    <Input size="xs" placeholder="e.g. 7901" value={form.bank_fee_nominal_code}
                      onChange={e => setForm(p => ({ ...p, bank_fee_nominal_code: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={1} minW="140px">
                    <FormLabel fontSize="xs" mb={0.5}>Fee Notes</FormLabel>
                    <Input size="xs" placeholder="e.g. SWIFT transfer fee" value={form.bank_fee_notes}
                      onChange={e => setForm(p => ({ ...p, bank_fee_notes: e.target.value }))} />
                  </FormControl>
                </>
              )}
            </HStack>
          </Box>
        )}

        {/* Internal notes */}
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Internal Notes
            <Text as="span" fontSize="9px" color="gray.400" ml={1}>(saved as a task note, visible in Bookkeeper)</Text>
          </FormLabel>
          <Textarea size="xs" rows={2} value={form.internal_notes} onChange={e => setForm(p => ({ ...p, internal_notes: e.target.value }))}
            placeholder="Internal notes about this invoice..." />
        </FormControl>

        <HStack w="100%" justify="flex-end">
          <Button size="xs" variant="ghost" onClick={exitForm}>Cancel</Button>
          <Button size="xs" bg={isIn ? 'orange.500' : 'brand.600'} color="white" _hover={{ bg: isIn ? 'orange.600' : 'brand.700' }} onClick={handleSave} isLoading={saving}>
            {editing ? 'Update Invoice' : isIn ? 'Record Invoice Received' : 'Create Invoice'}
            {isIn && !editing && <Text as="span" fontSize="8px" ml={1} opacity={0.8}>↑ upload follows</Text>}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  return (
    <>
    {/* ── Edit / create as a full-page surface (a real page, not a modal) ── */}
    {isOpen && (creating || editing) && (
      <Box position="fixed" inset={0} bg="gray.50" zIndex={1400} overflowY="auto">
        <Flex position="sticky" top={0} zIndex={2} bg="white" borderBottom="1px" borderColor="gray.200"
          boxShadow="sm" px={6} py={3} justify="space-between" align="center">
          <HStack spacing={3} minW={0}>
            <Button size="sm" leftIcon={<FiArrowLeft />} variant="ghost" onClick={exitForm}>Back to list</Button>
            <Text fontSize="lg" fontWeight="700" isTruncated>
              {editing ? 'Edit Invoice' : isIn ? 'Record Invoice Received' : 'New Invoice'}
              <Text as="span" fontSize="sm" fontWeight="500" color="gray.500" ml={2}>
                {isIn ? 'Costs (£ Out)' : 'Client (£ In)'}
              </Text>
            </Text>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <Button size="sm" variant="ghost" onClick={exitForm}>Cancel</Button>
            <Button size="sm" bg={isIn ? 'orange.500' : 'brand.600'} color="white"
              _hover={{ bg: isIn ? 'orange.600' : 'brand.700' }} onClick={handleSave} isLoading={saving}>
              {editing ? 'Update Invoice' : isIn ? 'Record Invoice Received' : 'Create Invoice'}
            </Button>
            <Tooltip label="Close">
              <IconButton aria-label="Close" icon={<FiX />} variant="ghost" size="sm" onClick={exitForm} />
            </Tooltip>
          </HStack>
        </Flex>
        <Box maxW="1100px" mx="auto" px={6} py={5}>
          {acctProcedures.length > 0 && (
            <Flex bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" px={3} py={2} mb={3}
              align="center" gap={2} flexWrap="wrap">
              <Text fontSize="14px">📋</Text>
              <Text fontSize="12px" color="orange.800" flex={1}>
                This client has {acctProcedures.length === 1 ? 'an Accounts procedure' : `${acctProcedures.length} Accounts procedures`} to
                follow when {isIn ? 'paying' : 'raising'} invoices.
              </Text>
              {acctProcedures.map(pp => (
                <Button key={pp.id} size="xs" colorScheme="orange" variant="outline" onClick={() => openProcPopup(pp.id)}>
                  View: {pp.name.length > 30 ? pp.name.slice(0, 30) + '…' : pp.name}
                </Button>
              ))}
            </Flex>
          )}
          {renderForm()}
        </Box>
      </Box>
    )}

    <Modal isOpen={isOpen && !(creating || editing)} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay /><ModalContent maxW="1200px" maxH="90vh">
        <ModalHeader fontSize="lg">
          <Flex justify="space-between" pr={10} align="center">
            <HStack spacing={2}>
              <Text>{isIn ? '📥 Costs — Quotes and Invoices (£ Out)' : '📤 Client Quotes and Invoices (£ In)'}</Text>
            </HStack>
            <HStack spacing={2}>
              <Checkbox size="sm" isChecked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
                <Text fontSize="12px">Show Deleted</Text></Checkbox>
              <Button size="sm" leftIcon={<FiPlus />} colorScheme={isIn ? 'orange' : 'green'} variant="outline"
                onClick={() => { const def=currencies.find(c=>c.is_default); setCreating(true); setEditing(false); setSelectedInvoice(null); setForm({ ...emptyForm, currency_id: def?String(def.id):'' }); }}>
                {isIn ? 'Record Invoice' : 'New Invoice'}
              </Button>
              <Button size="sm" variant="ghost" leftIcon={<FiX />} onClick={onClose}>Close</Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>

          {/* Accounts-procedure notification: when this project's client
              has one or more Accounts-category procedures, surface a
              non-blocking banner with a pop-up viewer — invoices for these
              clients have a defined process to follow. */}
          {acctProcedures.length > 0 && (
            <Flex bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" px={3} py={2} mb={3}
              align="center" gap={2} flexWrap="wrap">
              <Text fontSize="14px">📋</Text>
              <Text fontSize="12px" color="orange.800" flex={1}>
                This client has {acctProcedures.length === 1
                  ? <>an <b>Accounts procedure</b> to follow when {isIn ? 'paying' : 'raising'} invoices</>
                  : <><b>{acctProcedures.length} Accounts procedures</b> to follow when {isIn ? 'paying' : 'raising'} invoices</>}.
              </Text>
              {acctProcedures.map(p => (
                <Button key={p.id} size="xs" colorScheme="orange" variant="outline"
                  onClick={() => openProcPopup(p.id)}>
                  View: {p.name.length > 30 ? p.name.slice(0, 30) + '…' : p.name}
                </Button>
              ))}
            </Flex>
          )}

          {/* Detail view */}
          {selectedInvoice && !creating && !editing && (
            <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
              <Flex justify="space-between" mb={2}>
                <HStack>
                  <Text fontSize="sm" fontWeight="600">{selectedInvoice.invoice_number || selectedInvoice.invoice_ref || 'Invoice'}</Text>
                  <Badge colorScheme={statusColor(selectedInvoice.status, isIn)} fontSize="9px">{statusLabel(selectedInvoice.status, isIn)}</Badge>
                  {selectedInvoice.deleted_at && <Badge colorScheme="red" fontSize="9px">DELETED</Badge>}
                </HStack>
                <HStack spacing={1}>
                  <Button size="xs" variant="ghost" onClick={() => setSelectedInvoice(null)}>← Back</Button>
                  <Button size="xs" leftIcon={<FiPrinter />} variant="outline" colorScheme="purple"
                    onClick={() => {
                      const url = `/api/invoices/${selectedInvoice.id}/html`;
                      const token = localStorage.getItem('token');
                      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.text())
                        .then(html => {
                          const w = window.open('', '_blank');
                          w.document.write(html);
                          w.document.close();
                        });
                    }}>
                    {selectedInvoice.attached_file_uuid ? 'View Invoice Request' : 'Print / PDF'}
                  </Button>
                  {/* Uploaded invoice document takes precedence over the
                      generated one: View opens it inline; Download saves it
                      under its ORIGINAL uploaded filename (dedicated
                      endpoint sets Content-Disposition — the raw file URL
                      would save as the internal stored name). */}
                  {selectedInvoice.attached_file_uuid && (<>
                    <Button size="xs" colorScheme="green" variant="outline" leftIcon={<FiFile size={10} />}
                      onClick={() => window.open(`/api/files/${selectedInvoice.attached_file_path}`, '_blank')}>
                      View Invoice
                    </Button>
                    <Button size="xs" colorScheme="green" variant="outline" leftIcon={<FiDownload size={10} />}
                      onClick={() => {
                        const t = localStorage.getItem('token');
                        window.open(`/api/files/${selectedInvoice.attached_file_uuid}/download?token=${t}`, '_blank');
                      }}>
                      Download Invoice
                    </Button>
                  </>)}
                  <Button size="xs" leftIcon={<FiRotateCcw size={10} />} variant="outline" colorScheme="blue"
                    onClick={() => copyInvoice(selectedInvoice.id)}>
                    Copy as New
                  </Button>
                  {!selectedInvoice.deleted_at && <>
                    <Button size="xs" leftIcon={<FiEdit2 />} variant="outline" onClick={() => requestEdit(selectedInvoice)}>Edit</Button>
                    <Button size="xs" variant="outline" colorScheme="teal"
                      onClick={async () => {
                        if (showResources) { setShowResources(false); return; }
                        setShowResources(true); setLoadingBookings(true);
                        try {
                          const { data: bks } = await api.get(`/resource-bookings?project_id=${projectId}&limit=100`);
                          const all = bks || [];
                          setInvoiceBookings(all.filter(b => b.invoice_id === selectedInvoice.id));
                          setAvailableBookings(all.filter(b => !b.invoice_id));
                        } catch(e) {}
                        setLoadingBookings(false);
                      }}>
                      📦 {invoiceBookings.length > 0 ? `Resources (${invoiceBookings.length})` : 'Link Resources'}
                    </Button>
                    <Button size="xs" variant="outline" colorScheme="orange"
                      onClick={() => setShowConditions(!showConditions)}>
                      🧾 {conditions.length > 0 ? `Conditions (${conditions.length})` : 'Add Condition'}
                    </Button>
                    <Button size="xs" leftIcon={<FiUpload />} colorScheme="teal" variant="outline"
                      onClick={() => setUploadModal({ invoiceId: selectedInvoice.id, isCreditNote: selectedInvoice.status === 'recv_credited', invoiceRef: selectedInvoice.invoice_ref })}>
                      {isIn ? 'Upload Invoice Doc' : 'Attach Doc'}
                    </Button>
                    <Button size="xs" leftIcon={<Text fontSize="11px" fontWeight="800">£</Text>} colorScheme="green" variant="outline" onClick={() => setShowPayment(!showPayment)}>Pay</Button>
                    <IconButton size="xs" icon={<FiTrash2 />} colorScheme="red" variant="ghost" onClick={() => setShowDeleteConfirm(selectedInvoice.id)} aria-label="Delete" />
                  </>}
                  {selectedInvoice.deleted_at && <Button size="xs" leftIcon={<FiRotateCcw />} colorScheme="blue" variant="outline" onClick={() => handleUndelete(selectedInvoice.id)}>Restore</Button>}
                </HStack>
              </Flex>

              <HStack fontSize="10px" color="gray.500" spacing={3} mb={1}>
                <Text>Issued: {selectedInvoice.issue_date && new Date(selectedInvoice.issue_date).toLocaleDateString('en-GB')}</Text>
                <Text>Due: {selectedInvoice.due_date && new Date(selectedInvoice.due_date).toLocaleDateString('en-GB')}</Text>
                {selectedInvoice.pay_by_date && <Text color="red.500">Pay by: {new Date(selectedInvoice.pay_by_date).toLocaleDateString('en-GB')}</Text>}
              </HStack>
              <HStack fontSize="11px" spacing={3} mb={2}>
                <Text fontWeight="600">Net: £{parseFloat(selectedInvoice.total_net||0).toFixed(2)}</Text>
                <Text>VAT: £{parseFloat(selectedInvoice.total_vat||0).toFixed(2)}</Text>
                <Text fontWeight="700" color={isIn ? 'orange.700' : 'brand.700'}>Gross: £{parseFloat(selectedInvoice.total_gross||0).toFixed(2)}</Text>
                <Text color="green.600">Paid: £{parseFloat(selectedInvoice.amount_paid||0).toFixed(2)}</Text>
              </HStack>

              {selectedInvoice.internal_notes && (
                <Box bg="yellow.50" p={2} borderRadius="md" mb={2} border="1px" borderColor="yellow.200">
                  <Text fontSize="9px" fontWeight="700" color="yellow.700" textTransform="uppercase" mb={0.5}>Internal Notes</Text>
                  <Text fontSize="11px">{selectedInvoice.internal_notes}</Text>
                </Box>
              )}

              {/* Linked Resource Bookings */}
              {showResources && (
                <Box bg="teal.50" border="1px" borderColor="teal.200" borderRadius="md" p={3} mb={3}>
                  <Text fontSize="11px" fontWeight="700" color="teal.700" mb={1}>Linked Resource Bookings</Text>
                  <Text fontSize="10px" color="gray.500" mb={2}>
                    Resource bookings linked to this invoice. Linking confirms the work is invoiced.
                  </Text>
                  {loadingBookings ? <Flex justify="center" py={2}><Spinner size="sm" /></Flex> : (<>
                    {invoiceBookings.length > 0 && (
                      <VStack spacing={1} mb={2} align="stretch">
                        {invoiceBookings.map(b => (
                          <HStack key={b.id} p={2} bg="white" borderRadius="md" border="1px" borderColor="teal.100">
                            <Box flex={1}>
                              <Text fontSize="11px" fontWeight="600">{b.title}</Text>
                              <HStack fontSize="9px" color="gray.400" spacing={2}>
                                {b.start_date && <Text>{new Date(b.start_date).toLocaleDateString('en-GB')}</Text>}
                                <Badge fontSize="8px" colorScheme={b.status==='confirmed'?'green':b.status==='tentative'?'orange':'gray'}>{b.status}</Badge>
                              </HStack>
                            </Box>
                            <IconButton size="xs" icon={<FiTrash2 size={11}/>} variant="ghost" colorScheme="red"
                              aria-label="Unlink booking"
                              onClick={async () => {
                                await api.put(`/resource-bookings/${b.id}/invoice`, { invoice_id: null });
                                setInvoiceBookings(p => p.filter(x => x.id !== b.id));
                                setAvailableBookings(p => [...p, {...b, invoice_id: null}]);
                              }} />
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    {availableBookings.length > 0 && (
                      <Select size="xs" placeholder="+ Link an existing booking..."
                        onChange={async e => {
                          if (!e.target.value) return;
                          const bid = e.target.value;
                          await api.put(`/resource-bookings/${bid}/invoice`, { invoice_id: selectedInvoice.id });
                          const booking = availableBookings.find(b => b.id === bid);
                          if (booking) {
                            setInvoiceBookings(p => [...p, {...booking, invoice_id: selectedInvoice.id}]);
                            setAvailableBookings(p => p.filter(b => b.id !== bid));
                          }
                          e.target.value = '';
                        }}>
                        {availableBookings.map(b => (
                          <option key={b.id} value={b.id}>{b.title}{b.start_date ? ` — ${new Date(b.start_date).toLocaleDateString('en-GB')}` : ''}</option>
                        ))}
                      </Select>
                    )}
                    {invoiceBookings.length === 0 && availableBookings.length === 0 && (
                      <Text fontSize="11px" color="gray.400">No unlinked resource bookings found for this project.</Text>
                    )}
                  </>)}
                </Box>
              )}

              {/* Invoice Status Conditions */}
              {showConditions && (
                <Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" p={3} mb={3}>
                  <Text fontSize="11px" fontWeight="700" color="orange.700" mb={1}>
                    Invoice Status Conditions (Task/Sub-task Dependencies)
                  </Text>
                  <Text fontSize="10px" color="gray.500" mb={2}>
                    When this invoice reaches a specific status, linked tasks are unblocked. These conditions appear on Gantt charts.
                  </Text>
                  {conditions.length > 0 && (
                    <VStack spacing={1} mb={2} align="stretch">
                      {conditions.map(c => (
                        <HStack key={c.id} p={2} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                          <Text fontSize="11px" flex={1}>
                            <b>{c.task_title}</b> — needs <Badge colorScheme={c.current_status === c.required_status ? 'green' : 'orange'} fontSize="8px">{c.required_status.replace(/_/g,' ')}</Badge>
                            {c.current_status === c.required_status ? <Badge colorScheme="green" fontSize="8px" ml={1}>✓ Met</Badge> : <Badge colorScheme="red" fontSize="8px" ml={1}>Unmet</Badge>}
                          </Text>
                          <IconButton size="xs" icon={<FiTrash2 size={11}/>} variant="ghost" colorScheme="red"
                            onClick={async () => { await api.delete(`/invoices/conditions/${c.id}`); const {data:conds} = await api.get(`/invoices/${selectedInvoice.id}/conditions`); setConditions(conds||[]); }}
                            aria-label="Remove condition" />
                        </HStack>
                      ))}
                    </VStack>
                  )}
                  <HStack spacing={2}>
                    <Select size="xs" value={condForm.required_status} onChange={e => setCondForm(p=>({...p,required_status:e.target.value}))} flex={2}>
                      <option value="paid_in_full">Paid in Full</option>
                      <option value="sent_for_payment">Sent for Payment</option>
                      <option value="invoiced_within_terms">Within Terms</option>
                      <option value="contracted">Contracted</option>
                      <option value="quotation">Quotation (Sent)</option>
                      <option value="quotation_not_sent">Quotation (Not Sent)</option>
                      <option value="po_issued">PO Issued</option>
                    </Select>
                    <Input size="xs" placeholder="Label (optional)" value={condForm.label} onChange={e => setCondForm(p=>({...p,label:e.target.value}))} flex={2} />
                    <Button size="xs" colorScheme="orange" onClick={async () => {
                      await api.post('/invoices/conditions', { invoice_id: selectedInvoice.id, task_id: taskId, required_status: condForm.required_status, label: condForm.label || null });
                      const {data:conds} = await api.get(`/invoices/${selectedInvoice.id}/conditions`);
                      setConditions(conds||[]);
                      setCondForm({ required_status: 'paid_in_full', label: '' });
                    }}>+ Add</Button>
                  </HStack>
                </Box>
              )}

              {/* Items */}
              {selectedInvoice.items?.length > 0 && (
                <Table size="sm" variant="simple" mb={3}><Thead><Tr>
                  <Th fontSize="9px" px={1}>Code</Th><Th fontSize="9px" px={1}>Description</Th>
                  <Th fontSize="9px" px={1} isNumeric>Net £</Th><Th fontSize="9px" px={1}>VAT%</Th>
                  <Th fontSize="9px" px={1}>Qty</Th><Th fontSize="9px" px={1} isNumeric>Gross</Th>
                </Tr></Thead><Tbody>
                  {selectedInvoice.items.map(item => { const qty=parseFloat(item.quantity||1); const net=parseFloat(item.net_price||0); const lineNet=qty*net; const vatPct=parseFloat(item.vat_rate||0); const lineVat=lineNet*(vatPct/100);
                    if (item.is_note) return (
                      // Sage convention: M code, text in Description, value
                      // columns blank.
                      <Tr key={item.id}>
                        <Td px={1} fontSize="10px" fontWeight="600">M</Td>
                        <Td px={1} fontSize="10px">{item.description || ''}</Td>
                        <Td px={1}></Td><Td px={1}></Td><Td px={1}></Td><Td px={1}></Td>
                      </Tr>);
                    return (
                    <Tr key={item.id}>
                      <Td px={1} fontSize="10px" color="gray.500">{item.item_code||'—'}</Td>
                      <Td px={1} fontSize="10px">
                        {item.description}
                        {item.custom_description && <Text fontSize="9px" color="gray.500" fontStyle="italic" mt={0.5}>{item.custom_description}</Text>}
                      </Td>
                      <Td px={1} fontSize="10px" isNumeric>£{net.toFixed(2)}</Td>
                      <Td px={1} fontSize="10px">{vatPct}%</Td>
                      <Td px={1} fontSize="10px">{qty}</Td>
                      <Td px={1} fontSize="10px" isNumeric fontWeight="600">£{(lineNet+lineVat).toFixed(2)}</Td>
                    </Tr>); })}
                </Tbody></Table>
              )}

              {/* Payments */}
              {selectedInvoice.payments?.length > 0 && (<>
                <Text fontSize="10px" fontWeight="700" textTransform="uppercase" color="gray.500" mb={1}>Payments</Text>
                {selectedInvoice.payments.map(p => (
                  <Flex key={p.id} p={1.5} border="1px" borderColor="green.100" borderRadius="sm" mb={1} fontSize="10px" align="center" justify="space-between">
                    <HStack spacing={2}>
                      <Badge colorScheme="green" fontSize="9px">£{parseFloat(p.amount).toFixed(2)}</Badge>
                      <Text color="gray.500">{new Date(p.payment_date).toLocaleDateString('en-GB')}</Text>
                      {p.from_bank_name && <Text color="gray.400">from: {p.from_bank_name}</Text>}
                      {p.reference && <Text color="gray.400">ref: {p.reference}</Text>}
                    </HStack>
                  </Flex>))}
              </>)}

              {/* Record Payment */}
              {showPayment && !selectedInvoice.deleted_at && (
                <Box bg="green.50" p={3} borderRadius="md" mt={3}>
                  <Text fontSize="11px" fontWeight="600" mb={2}>Record Payment</Text>
                  <VStack spacing={2}>
                    <HStack w="100%" spacing={2}>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Amount £</FormLabel><Input size="xs" type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} /></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Date</FormLabel><Input size="xs" type="date" value={payForm.payment_date} onChange={e => setPayForm(p => ({ ...p, payment_date: e.target.value }))} /></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Reference</FormLabel><Input size="xs" value={payForm.reference} onChange={e => setPayForm(p => ({ ...p, reference: e.target.value }))} /></FormControl>
                    </HStack>
                    <HStack w="100%" spacing={2}>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Sage / Bank Ref</FormLabel><Input size="xs" value={payForm.sage_reference} onChange={e => setPayForm(p => ({ ...p, sage_reference: e.target.value }))} /></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Sender Ref</FormLabel><Input size="xs" value={payForm.sender_reference} onChange={e => setPayForm(p => ({ ...p, sender_reference: e.target.value }))} /></FormControl>
                    </HStack>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>{isIn ? 'Pay From (Our Account)' : 'Receive Into'}</FormLabel>
                      <Select size="xs" value={isIn ? payForm.from_bank_account_id : payForm.to_bank_account_id} onChange={e => setPayForm(p => isIn ? ({ ...p, from_bank_account_id: e.target.value }) : ({ ...p, to_bank_account_id: e.target.value }))}>
                        <option value="">—</option>
                        {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.bank_name} {b.currency_code?`(${b.currency_code})`:''}</option>)}
                      </Select></FormControl>
                    <Button size="xs" bg="brand.600" color="white" _hover={{ bg:'brand.700' }} onClick={handlePayment} isLoading={saving} alignSelf="flex-end">Record Payment</Button>
                  </VStack>
                </Box>
              )}

              {/* Bank Transactions (reconciliation) */}
              {isIn && (
                <Box mt={3} border="1px solid" borderColor="blue.200" borderRadius="md" overflow="hidden">
                  <Flex bg="blue.50" px={3} py={2} justify="space-between" align="center">
                    <Text fontSize="10px" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="wide">
                      Bank Transactions
                      {bankTransactions.length > 0 && <Text as="span" ml={2} color="blue.500">({bankTransactions.length})</Text>}
                    </Text>
                    {!selectedInvoice.deleted_at && (
                      <Button size="xs" variant="outline" colorScheme="blue"
                        onClick={() => setShowBankTxForm(p => !p)}>
                        {showBankTxForm ? 'Cancel' : '+ Link Transaction'}
                      </Button>
                    )}
                  </Flex>

                  {bankTransactions.length > 0 && (
                    <Box px={3} pb={2}>
                      {bankTransactions.map(bt => (
                        <Flex key={bt.id} py={1.5} borderBottom="1px solid" borderColor="gray.100"
                          fontSize="10px" align="center" justify="space-between">
                          <HStack spacing={2} flex={1}>
                            <Badge fontSize="8px" colorScheme={
                              bt.transaction_type === 'principal' ? 'blue' :
                              bt.transaction_type === 'bank_fee' ? 'orange' :
                              bt.transaction_type === 'fx_conversion' ? 'purple' : 'gray'
                            }>{bt.transaction_type.replace('_', ' ')}</Badge>
                            <Text fontWeight="700">£{parseFloat(bt.amount_gbp).toFixed(2)}</Text>
                            <Text color="gray.500">{new Date(bt.transaction_date).toLocaleDateString('en-GB')}</Text>
                            <Text color="gray.400">{bt.bank_name || bt.account_name}</Text>
                            {bt.foreign_amount && <Text color="purple.600">{bt.currency_code}{parseFloat(bt.foreign_amount).toFixed(2)} @ {parseFloat(bt.exchange_rate).toFixed(4)}</Text>}
                            {bt.reference && <Text color="gray.400">ref: {bt.reference}</Text>}
                            {bt.reconciled_at && <Badge fontSize="8px" colorScheme="green">Reconciled</Badge>}
                          </HStack>
                          <IconButton icon={<FiTrash2 size={10} />} size="xs" variant="ghost" colorScheme="red" aria-label="Remove"
                            onClick={async () => {
                              await api.delete(`/invoices/${selectedInvoice.id}/bank-transactions/${bt.id}`);
                              fetchBankTransactions(selectedInvoice.id);
                            }} />
                        </Flex>
                      ))}
                    </Box>
                  )}

                  {showBankTxForm && (
                    <Box bg="blue.50" px={3} pb={3} pt={2}>
                      <VStack spacing={2} align="stretch">
                        <HStack spacing={2}>
                          <FormControl maxW="150px">
                            <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                            <Select size="xs" value={btForm.transaction_type}
                              onChange={e => setBtForm(p => ({ ...p, transaction_type: e.target.value }))}>
                              <option value="principal">Principal payment</option>
                              <option value="bank_fee">Bank fee</option>
                              <option value="fx_conversion">FX conversion</option>
                              <option value="other">Other</option>
                            </Select>
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Bank Account</FormLabel>
                            <Select size="xs" value={btForm.bank_account_id}
                              onChange={e => setBtForm(p => ({ ...p, bank_account_id: e.target.value }))}>
                              <option value="">— Select account —</option>
                              {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name || b.bank_name}</option>)}
                            </Select>
                          </FormControl>
                          <FormControl maxW="110px">
                            <FormLabel fontSize="xs" mb={0.5}>Date</FormLabel>
                            <Input size="xs" type="date" value={btForm.transaction_date}
                              onChange={e => setBtForm(p => ({ ...p, transaction_date: e.target.value }))} />
                          </FormControl>
                          <FormControl maxW="100px">
                            <FormLabel fontSize="xs" mb={0.5}>Amount £</FormLabel>
                            <Input size="xs" type="number" step="0.01" value={btForm.amount_gbp} placeholder="0.00"
                              onChange={e => setBtForm(p => ({ ...p, amount_gbp: e.target.value }))} />
                          </FormControl>
                        </HStack>
                        <HStack spacing={2}>
                          <FormControl maxW="130px">
                            <FormLabel fontSize="xs" mb={0.5}>Foreign Currency</FormLabel>
                            <Select size="xs" value={btForm.foreign_currency_id}
                              onChange={e => setBtForm(p => ({ ...p, foreign_currency_id: e.target.value }))}>
                              <option value="">None</option>
                              {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                            </Select>
                          </FormControl>
                          {btForm.foreign_currency_id && (
                            <>
                              <FormControl maxW="110px">
                                <FormLabel fontSize="xs" mb={0.5}>Foreign Amount</FormLabel>
                                <Input size="xs" type="number" step="0.01" value={btForm.foreign_amount} placeholder="0.00"
                                  onChange={e => setBtForm(p => ({ ...p, foreign_amount: e.target.value }))} />
                              </FormControl>
                              <FormControl maxW="110px">
                                <FormLabel fontSize="xs" mb={0.5}>Exchange Rate</FormLabel>
                                <Input size="xs" type="number" step="0.000001" value={btForm.exchange_rate} placeholder="e.g. 0.789"
                                  onChange={e => setBtForm(p => ({ ...p, exchange_rate: e.target.value }))} />
                              </FormControl>
                            </>
                          )}
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Reference</FormLabel>
                            <Input size="xs" value={btForm.reference} placeholder="Bank ref / TXN id"
                              onChange={e => setBtForm(p => ({ ...p, reference: e.target.value }))} />
                          </FormControl>
                        </HStack>
                        <Flex justify="flex-end">
                          <Button size="xs" colorScheme="blue" isLoading={saving}
                            onClick={async () => {
                              if (!btForm.bank_account_id || !btForm.amount_gbp || !btForm.transaction_date) {
                                toast({ title: 'Bank account, amount and date required', status: 'warning', duration: 2000 }); return;
                              }
                              try {
                                await api.post(`/invoices/${selectedInvoice.id}/bank-transactions`, {
                                  ...btForm,
                                  foreign_currency_id: btForm.foreign_currency_id || null,
                                  foreign_amount: btForm.foreign_amount || null,
                                  exchange_rate: btForm.exchange_rate || null,
                                });
                                await fetchBankTransactions(selectedInvoice.id);
                                setShowBankTxForm(false);
                                setBtForm({ bank_account_id:'', transaction_type:'principal', transaction_date: new Date().toISOString().split('T')[0], amount_gbp:'', foreign_currency_id:'', foreign_amount:'', exchange_rate:'', reference:'', notes:'' });
                                toast({ title: 'Bank transaction linked', status: 'success', duration: 1500 });
                              } catch(e) {
                                toast({ title: 'Failed to save', status: 'error', duration: 2000 });
                              }
                            }}>
                            Save Transaction
                          </Button>
                        </Flex>
                      </VStack>
                    </Box>
                  )}

                  {bankTransactions.length === 0 && !showBankTxForm && (
                    <Box px={3} py={2}>
                      <Text fontSize="10px" color="gray.400" fontStyle="italic">No bank transactions linked yet. Click "+ Link Transaction" to associate bank statement entries for reconciliation.</Text>
                    </Box>
                  )}
                </Box>
              )}

              {showDeleteConfirm === selectedInvoice.id && (
                <Box bg="red.50" p={3} borderRadius="md" mt={3}>
                  <Text fontSize="11px" fontWeight="600" color="red.700" mb={2}>Delete — provide reason:</Text>
                  <Textarea size="xs" rows={2} value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Reason..." mb={2} />
                  <HStack justify="flex-end">
                    <Button size="xs" variant="ghost" onClick={() => { setShowDeleteConfirm(null); setDeleteReason(''); }}>Cancel</Button>
                    <Button size="xs" colorScheme="red" onClick={() => handleDelete(selectedInvoice.id)}>Confirm Delete</Button>
                  </HStack>
                </Box>
              )}

              <Box mt={3}>
                <FileUpload entityType="invoice" entityId={selectedInvoice.id} folder="invoices" accept="image/*,.pdf" label="Invoice Documents" />
              </Box>
            </Box>
          )}

          {/* List */}
          {!creating && !editing && !selectedInvoice && (
            invoices.length === 0 ? (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No invoices yet.</Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {invoices.map(inv => {
                  const isExpanded = expandedInvoiceId === inv.id;
                  const isLocked = ['sent_for_payment','invoiced_within_terms','paid_in_part','paid_in_full',
                    'overdue','very_overdue','written_off','send_for_debt_collection','receipt',
                    'recv_pay_within_terms','recv_overdue','recv_receipt','recv_paid_in_full'].includes(inv.status);
                  const isScheduled = ['quotation_not_sent','future_contractual'].includes(inv.status);
                  const isParent = inv.billing_type && inv.billing_type !== 'one_off' && !inv.parent_invoice_id;
                  return (
                  <Box key={inv.id} border="1px" borderColor={inv.deleted_at ? 'red.200' : isScheduled ? 'purple.200' : 'gray.200'}
                    borderRadius="md" bg={inv.deleted_at ? 'red.50' : isScheduled ? 'purple.50' : 'white'} opacity={inv.deleted_at ? 0.6 : 1}
                    overflow="hidden">
                    {/* Main row */}
                    <Flex p={3} justify="space-between" align="center">
                      <HStack flex={1} cursor="pointer" _hover={{ color:'teal.600' }}
                        onClick={() => toggleExpand(inv.id)}>
                        <Box as={isExpanded ? FiChevronUp : FiChevronDown} size={14} color="gray.400" flexShrink={0} />
                        <Box>
                          <HStack spacing={2} mb={1}>
                            <Text fontSize="15px" fontWeight="700">{inv.invoice_number || inv.invoice_ref || '—'}</Text>
                            {inv.deleted_at && <Badge colorScheme="red" fontSize="9px">DELETED</Badge>}
                            {isScheduled && <Badge colorScheme="purple" fontSize="9px" variant="subtle">SCHEDULED</Badge>}
                            {isParent && <Badge colorScheme="teal" fontSize="9px" variant="subtle">{inv.billing_type?.toUpperCase()}</Badge>}
                          </HStack>
                          <HStack fontSize="12px" color="gray.600" spacing={4} flexWrap="wrap">
                            <Text>{isScheduled ? 'Due:' : 'Issued:'} {inv.issue_date && new Date(inv.issue_date).toLocaleDateString('en-GB')}</Text>
                            {inv.pay_by_date && <Text color="red.500">Pay by: {new Date(inv.pay_by_date).toLocaleDateString('en-GB')}</Text>}
                            {inv.supplier_name && <Text color="orange.500">{inv.supplier_name}</Text>}
                            <Text fontWeight="700" color="gray.800">£{parseFloat(inv.total_gross||0).toFixed(2)}</Text>
                            {parseFloat(inv.amount_paid||0) > 0 && <Text color="green.600">Paid: £{parseFloat(inv.amount_paid).toFixed(2)}</Text>}
                          </HStack>
                        </Box>
                      </HStack>
                      <HStack spacing={1} flexShrink={0}>
                        <Badge colorScheme={statusColor(inv.status, isIn)} fontSize="9px">{statusLabel(inv.status, isIn)}</Badge>
                        {/* Part paid AND past due — server computes is_overdue */}
                        {!!inv.is_overdue && inv.status === 'paid_in_part' && (
                          <Badge colorScheme="red" fontSize="9px">OVERDUE</Badge>
                        )}
                        {!inv.deleted_at && !isLocked && (
                          <Select size="xs" w="140px" fontSize="9px" value={inv.status}
                            onClick={e => e.stopPropagation()}
                            onChange={e => { e.stopPropagation(); handleStatusChange(inv.id, e.target.value); }}>
                            {Object.entries(statusOpts).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                          </Select>
                        )}
                        {!inv.deleted_at && !isLocked && (
                          <Tooltip label="Edit invoice">
                            <IconButton size="xs" icon={<FiEdit2 size={11} />} variant="ghost" colorScheme="teal"
                              aria-label="Edit invoice"
                              onClick={async e => { e.stopPropagation(); const { data } = await api.get(`/invoices/${inv.id}`); setSelectedInvoice(data); requestEdit(data); }} />
                          </Tooltip>
                        )}
                        {!inv.deleted_at && isLocked && (
                          <Tooltip label="Invoice is locked — click to edit anyway">
                            <IconButton size="xs" icon={<FiEdit2 size={11} />} variant="ghost" colorScheme="orange"
                              aria-label="Edit locked invoice"
                              onClick={async e => { e.stopPropagation(); const { data } = await api.get(`/invoices/${inv.id}`); setSelectedInvoice(data); requestEdit(data); }} />
                          </Tooltip>
                        )}
                        {isParent && !inv.deleted_at && (
                          <Tooltip label="Regenerate future scheduled invoices">
                            <IconButton size="xs" icon={<FiRefreshCw size={11} />} variant="ghost" colorScheme="purple"
                              isLoading={regenerating}
                              aria-label="Regenerate"
                              onClick={async e => {
                                e.stopPropagation();
                                if (!window.confirm('Delete all pending scheduled invoices and regenerate from the subscription spec?')) return;
                                setRegenerating(true);
                                try {
                                  const { data } = await api.post(`/invoices/${inv.id}/regenerate`);
                                  toast({ title: `Regenerated: ${data.generated} invoice(s)`, status: 'success', duration: 3000 });
                                  await fetchInvoices();
                                } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
                                finally { setRegenerating(false); }
                              }} />
                          </Tooltip>
                        )}
                      </HStack>
                    </Flex>

                    {/* Expanded detail panel */}
                    {isExpanded && (
                      <Box borderTop="1px" borderColor="gray.100" bg="gray.50" p={3}>
                        <HStack spacing={2} mb={2} flexWrap="wrap">
                          <Button size="xs" leftIcon={<FiEdit2 size={10} />} colorScheme="teal" variant="outline"
                            isDisabled={!!inv.deleted_at}
                            onClick={async () => { const { data } = await api.get(`/invoices/${inv.id}`); setSelectedInvoice(data); requestEdit(data); }}>
                            {isLocked ? '🔒 Edit (locked)' : 'Edit'}
                          </Button>
                          <Button size="xs" variant="outline"
                            onClick={() => { const url = `/api/invoices/${inv.id}/html`; const t = localStorage.getItem('token'); window.open(`${url}?token=${t}`, '_blank'); }}>
                            {inv.attached_file_uuid ? 'View Invoice Request' : 'Print / PDF'}
                          </Button>
                          {inv.attached_file_uuid && (<>
                            <Button size="xs" colorScheme="green" variant="outline"
                              onClick={() => window.open(`/api/files/${inv.attached_file_path}`, '_blank')}>
                              View Invoice
                            </Button>
                            <Button size="xs" colorScheme="green" variant="outline"
                              onClick={() => {
                                const t = localStorage.getItem('token');
                                window.open(`/api/files/${inv.attached_file_uuid}/download?token=${t}`, '_blank');
                              }}>
                              Download Invoice
                            </Button>
                          </>)}
                          <Button size="xs" leftIcon={<FiRotateCcw size={10} />} variant="outline" colorScheme="blue"
                            onClick={() => copyInvoice(inv.id)}>
                            Copy as New
                          </Button>
                          {!inv.deleted_at && (
                            <Button size="xs" colorScheme="red" variant="ghost"
                              onClick={() => setShowDeleteConfirm(inv.id)}>
                              Delete
                            </Button>
                          )}
                        </HStack>
                        {/* Received-invoice document attachment (versioned) */}
                        {isIn && !inv.deleted_at && (
                          <InvoiceDocumentCell invoice={inv} onChanged={fetchInvoices} />
                        )}
                        {/* Line items summary */}
                        {(() => { const items = expandedItems[inv.id]; return items === undefined ? (
                          <Text fontSize="10px" color="gray.400">Loading…</Text>
                        ) : items.length > 0 ? (
                          <Box overflowX="auto">
                            <Table size="xs" variant="simple">
                              <Thead><Tr>
                                <Th fontSize="9px" px={1}>Description</Th>
                                <Th fontSize="9px" px={1} isNumeric>Qty</Th>
                                <Th fontSize="9px" px={1} isNumeric>Net</Th>
                                <Th fontSize="9px" px={1} isNumeric>Gross</Th>
                              </Tr></Thead>
                              <Tbody>
                                {items.map((item, i) => {
                                  const net = (parseFloat(item.quantity)||1) * (parseFloat(item.net_price)||0);
                                  const gross = net * (1 + (parseFloat(item.vat_rate)||0)/100);
                                  if (item.is_note) return (
                                    // Sage-style: message text only, values blank.
                                    <Tr key={i}>
                                      <Td px={1} fontSize="10px">{item.description || ''}</Td>
                                      <Td px={1}></Td><Td px={1}></Td><Td px={1}></Td>
                                    </Tr>);
                                  return <Tr key={i}>
                                    <Td px={1} fontSize="10px">
                                      {item.description}
                                      {item.custom_description && <Text fontSize="9px" color="gray.400" fontStyle="italic">{item.custom_description}</Text>}
                                    </Td>
                                    <Td px={1} fontSize="10px" isNumeric>{parseFloat(item.quantity||1)}</Td>
                                    <Td px={1} fontSize="10px" isNumeric>£{net.toFixed(2)}</Td>
                                    <Td px={1} fontSize="10px" fontWeight="600" isNumeric>£{gross.toFixed(2)}</Td>
                                  </Tr>;
                                })}
                              </Tbody>
                            </Table>
                          </Box>
                        ) : (
                          <Text fontSize="10px" color="gray.400">No line items.</Text>
                        ); })()}
                      </Box>
                    )}
                  </Box>
                  );
                })}
              </VStack>
            )
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>

    {/* Resource booking popup */}
    {resourceBookingPopup && (
      <Modal isOpen={true} onClose={() => setResourceBookingPopup(null)} size="md">
        <ModalOverlay /><ModalContent>
          <ModalHeader fontSize="md">Schedule Resources
            <Text fontSize="11px" fontWeight="400" color="gray.500" mt={0.5}>"{resourceBookingPopup.template.description}" — set dates or skip.</Text>
          </ModalHeader><ModalCloseButton />
          <ModalBody><VStack spacing={3}>
            {resourceBookingPopup.requirements.map(req => { const entry=resourceBookingPopup.dates[req.id]||{}; const update=(field,val)=>setResourceBookingPopup(p=>({...p,dates:{...p.dates,[req.id]:{...p.dates[req.id],[field]:val}}})); return (
              <Box key={req.id} w="100%" bg={entry.skip?'gray.50':'teal.50'} p={3} borderRadius="md" border="1px" borderColor={entry.skip?'gray.200':'teal.200'} opacity={entry.skip?0.6:1}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="12px" fontWeight="700" color="teal.800">{req.resource_name || `Resource #${req.resource_id}`}</Text>
                  <Checkbox size="sm" isChecked={!!entry.skip} onChange={e=>update('skip',e.target.checked)} colorScheme="gray"><Text fontSize="11px" color="gray.500">Skip</Text></Checkbox>
                </Flex>
                {!entry.skip && <HStack spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Date (optional)</FormLabel><Input size="sm" type="date" value={entry.date||''} onChange={e=>update('date',e.target.value)} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Time</FormLabel><Input size="sm" type="time" value={entry.time||''} onChange={e=>update('time',e.target.value)} /></FormControl>
                </HStack>}
              </Box>);
            })}
          </VStack></ModalBody>
          <ModalFooter><HStack spacing={2}>
            <Button size="sm" variant="ghost" onClick={() => setResourceBookingPopup(null)}>Skip All</Button>
            <Button size="sm" bg="teal.500" color="white" _hover={{ bg:'teal.600' }} onClick={confirmResourceBookings}>Create Bookings</Button>
          </HStack></ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Invoice document upload modal (shown after creating IN invoice) */}
    {uploadModal && (
      <InvoiceUploadModal
        invoiceId={uploadModal.invoiceId}
        projectId={projectId}
        taskId={taskId}
        invoiceRef={uploadModal.invoiceRef}
        isCreditNote={uploadModal.isCreditNote}
        isOpen={true}
        onClose={() => { setUploadModal(null); toast({ title: 'Invoice recorded', status: 'success', duration: 1500 }); }}
      />
    )}

    {/* ── Locked-edit confirmation ── */}
    {lockedEditConfirm && (
      <Modal isOpen={true} onClose={() => setLockedEditConfirm(null)} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" pb={1}>Edit Locked Invoice</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} mb={3}>
              <Text fontSize="13px" fontWeight="700" color="orange.700" mb={1}>⚠ This invoice was previously locked for editing</Text>
              <Text fontSize="12px" color="orange.600">
                Status: <strong>{lockedEditConfirm.status?.replace(/_/g,' ')}</strong>.
                Editing a sent or paid invoice may affect your financial records and audit trail.
              </Text>
            </Box>
            <Text fontSize="12px" color="gray.600">Are you sure you want to unlock and edit this invoice?</Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setLockedEditConfirm(null)}>Cancel</Button>
            <Button size="sm" colorScheme="orange" onClick={() => {
              const inv = lockedEditConfirm;
              setLockedEditConfirm(null);
              startEditDirect(inv);
            }}>
              Yes, Edit Anyway
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* ── Copy as New — destination picker ── */}
    {copyModal && (
      <Modal isOpen={true} onClose={() => setCopyModal(null)} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" pb={1}>
            Copy Invoice — Choose Destination
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={2} mb={4}>
              <Text fontSize="11px" color="blue.700">
                Copying: <strong>{copyModal.inv.invoice_number || copyModal.inv.invoice_ref || `#${copyModal.inv.id}`}</strong>
                {' '}— £{parseFloat(copyModal.inv.total_gross||0).toFixed(2)}
              </Text>
              <Text fontSize="10px" color="blue.500" mt={0.5}>Issue date will be set to today. File attachments will not be copied.</Text>
            </Box>

            <RadioGroup value={copyDest} onChange={val => {
              setCopyDest(val);
              setCopyOtherProjectId('');
              setCopyOtherTaskId('');
              setCopyOtherTasks([]);
              setCopyOtherProjects([]);
              if (val === 'this_project') setCopyTargetTaskId(taskId || '');
            }}>
              <Stack spacing={3}>
                <Radio value="this_project" colorScheme="blue">
                  <Box>
                    <Text fontSize="13px" fontWeight="600">Assign to a task within this project</Text>
                    <Text fontSize="10px" color="gray.500">Pick any task in the current project (or keep it project-level)</Text>
                  </Box>
                </Radio>
                <Radio value="another_project" colorScheme="blue">
                  <Box>
                    <Text fontSize="13px" fontWeight="600">Find a task in another live project</Text>
                    <Text fontSize="10px" color="gray.500">Search any project not marked DONE, then choose its task</Text>
                  </Box>
                </Radio>
              </Stack>
            </RadioGroup>

            {/* Assign within this project — every task listed */}
            {copyDest === 'this_project' && (
              <FormControl mt={4}>
                <FormLabel fontSize="xs" mb={1}>Select Task</FormLabel>
                <Select size="sm" value={copyTargetTaskId}
                  onChange={e => setCopyTargetTaskId(e.target.value)}>
                  <option value="">— Project level (no task) —</option>
                  {copyProjectTasks.map(t => (
                    <option key={t.id} value={t.id}>{t.task_title || `Task #${t.id}`}</option>
                  ))}
                </Select>
                {copyProjectTasks.length === 0 && (
                  <Text fontSize="10px" color="gray.400" mt={1} fontStyle="italic">
                    This project has no tasks — the copy will be added at project level.
                  </Text>
                )}
              </FormControl>
            )}

            {/* Another live project — typeahead search → project → task */}
            {copyDest === 'another_project' && (
              <VStack spacing={3} mt={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Search Live Projects</FormLabel>
                  <Input size="sm" placeholder="Type to search projects…"
                    onChange={async e => {
                      const q = e.target.value.trim();
                      setCopyOtherProjectId('');
                      setCopyOtherTaskId('');
                      setCopyOtherTasks([]);
                      if (q.length < 1) { setCopyOtherProjects([]); return; }
                      try {
                        const { data } = await api.get(`/projects/search?q=${encodeURIComponent(q)}&full=1&live=1`);
                        setCopyOtherProjects((Array.isArray(data) ? data : []).filter(p => String(p.id) !== String(projectId)));
                      } catch(e) {}
                    }} />
                </FormControl>
                {copyOtherProjects.length > 0 && (
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Select Project</FormLabel>
                    <Select size="sm" value={copyOtherProjectId}
                      onChange={async e => {
                        const pid = e.target.value;
                        setCopyOtherProjectId(pid);
                        setCopyOtherTaskId('');
                        setCopyOtherTasks([]);
                        if (pid) {
                          setCopyTasksLoading(true);
                          try {
                            const { data } = await api.get(`/tasks?project_id=${encodeURIComponent(pid)}`);
                            setCopyOtherTasks(Array.isArray(data) ? data : []);
                          } catch(err) {
                            toast({ title: 'Could not load tasks for that project', status: 'error', duration: 2500 });
                          } finally { setCopyTasksLoading(false); }
                        }
                      }}
                      placeholder="— choose project —">
                      {copyOtherProjects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.project_name}{p.company_name ? ` (${p.company_name})` : ''}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {copyOtherProjectId && (
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Select Task</FormLabel>
                    <Select size="sm" value={copyOtherTaskId}
                      onChange={e => setCopyOtherTaskId(e.target.value)}
                      isDisabled={copyTasksLoading}>
                      <option value="">— Project level (no task) —</option>
                      {copyOtherTasks.map(t => (
                        <option key={t.id} value={t.id}>{t.task_title || `Task #${t.id}`}</option>
                      ))}
                    </Select>
                    {copyTasksLoading
                      ? <Text fontSize="10px" color="gray.400" mt={1}><Spinner size="xs" mr={1} />Loading tasks…</Text>
                      : (copyOtherTasks.length === 0 && (
                          <Text fontSize="10px" color="gray.400" mt={1} fontStyle="italic">
                            No tasks in this project — the copy will be added at project level.
                          </Text>
                        ))}
                  </FormControl>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setCopyModal(null)}>Cancel</Button>
            <Button size="sm" colorScheme="blue" isLoading={copyLoading} loadingText="Copying…"
              onClick={executeCopy}>
              Create Copy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Accounts-procedure pop-up viewer — its own layer, non-blocking:
        close it and carry on with the invoice underneath. */}
    {procPopup && (
      <Modal isOpen={true} onClose={() => setProcPopup(null)} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent maxH="85vh">
          <ModalHeader fontSize="md">📋 {procPopup.name}
            {procPopup.category_name && <Badge ml={2} colorScheme="teal" fontSize="9px">{procPopup.category_name}</Badge>}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" onWheel={e => e.stopPropagation()}><ProcedureViewer procedure={procPopup} /></ModalBody>
          <ModalFooter><Button size="sm" onClick={() => setProcPopup(null)}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>
    )}
  </>
  );
}
